import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Plus, Edit, Trash2, Settings, Wrench, X, Package, Printer, Search, Upload, Download, FileDown, Sparkles, Filter, Camera, FileImage, MoreVertical, Eye, Loader, FileSpreadsheet, BadgePercent, ChevronsUpDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import MaintenanceReport from '../components/MaintenanceReport';
import { formatDateDisplay } from '../utils/dateUtils';
import { downloadExcel, downloadPDF } from '../utils/downloadHelpers';
import { toast } from 'sonner';
import FileUpload from '../components/FileUpload';
import BulkUpload from '../components/BulkUpload';
import AutoFillInput from '../components/AutoFillInput';
import { downloadRepairItemTemplate } from '../utils/bulkUploadUtils';
import { generateMaintenancePDF } from '../utils/maintenancePdfUtils';
import { useTranslation } from 'react-i18next';
import { SortButton, sortData, DateRangeFilter, SearchFilters, filterData } from '../components/DataTableWithFilters';
// Removed grouped records - using flat list with pagination instead
import { formatErrorMessage } from '../utils/formatError';
import { usePermissions } from '../utils/permissionUtils';
import { CurrencyDisplay } from '../components/DirhamSymbol';
import { ScrollableTableContainer } from '../components/ui/scrollable-table-container';
import useDeviceDetection from '../hooks/useDeviceDetection';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import PlatePair from '../components/PlatePair';

// Simple table row renderer without grouping

const Maintenance = ({ user }) => {
  const { t } = useTranslation();
  const [sortConfig, setSortConfig] = useState({ field: 'date', direction: 'desc' });
  const { isMobile, isTablet } = useDeviceDetection();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const ITEMS_PER_PAGE = 50;
  
  // Server-side search
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  // Stats for summary (fetched separately from server)
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    in_progress: 0,
    pending: 0,
    total_cost: 0,
    total_amc_cost: 0,
    total_admin_discount: 0,
    highest_bike_month: null,
    current_month: ''
  });
  
  // Summary detail dialog
  const [summaryDetailOpen, setSummaryDetailOpen] = useState(false);
  const [summaryDetailData, setSummaryDetailData] = useState(null);
  const [summaryDetailLoading, setSummaryDetailLoading] = useState(false);
  
  // Permission checks for module and sub-pages
  const permissions = usePermissions(user, 'maintenance');
  const canCreate = permissions.canCreate;
  const canEdit = permissions.canEdit;
  const canDelete = permissions.canDelete;
  const canViewRepairItems = permissions.canViewSubPage('repairItems');
  const canCreateRepairItems = permissions.canCreateSubPage('repairItems');
  const canEditRepairItems = permissions.canEditSubPage('repairItems');
  const canDeleteRepairItems = permissions.canDeleteSubPage('repairItems');
  const canViewTechnicians = permissions.canViewSubPage('technicians');
  const canCreateTechnicians = permissions.canCreateSubPage('technicians');
  const canDeleteTechnicians = permissions.canDeleteSubPage('technicians');

  // Helper function to generate page numbers for pagination
  const getPageNumbers = (totalPages) => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 4) {
        pages.push('...');
      }
      const start = Math.max(2, currentPage - 2);
      const end = Math.min(totalPages - 1, currentPage + 2);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 3) {
        pages.push('...');
      }
      pages.push(totalPages);
    }
    return pages;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedRecords([]);
    fetchData(page);
  };

  const [spares, setSpares] = useState([]);
  const [spareRequestDialogOpen, setSpareRequestDialogOpen] = useState(false);
  const [spareRequestData, setSpareRequestData] = useState({
    spare_id: '',
    spare_name: '',
    part_number: '',
    quantity_requested: 1,
    notes: ''
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [maintenanceRecords, setMaintenanceRecords] = useState([]);
  const [filteredMaintenanceRecords, setFilteredMaintenanceRecords] = useState([]);
  const [repairItems, setRepairItems] = useState([]);
  const [motorcycles, setMotorcycles] = useState([]);
  const [riders, setRiders] = useState([]);
  const [lessees, setLessees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [repairItemDialogOpen, setRepairItemDialogOpen] = useState(false);
  const [bulkUploadRepairItemsOpen, setBulkUploadRepairItemsOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingRepairItemId, setEditingRepairItemId] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [activeTab, setActiveTab] = useState('records');
  const [repairItemSearch, setRepairItemSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter state (matching Salik pattern)
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    lessee: '',
    rider: '',
    bike: '',
    technician: '',
    repairItem: ''  // filter by repair item id — matches records whose
                    // repair_items[] contains a matching repair_item_id
  });
  const [repairItemFilterOpen, setRepairItemFilterOpen] = useState(false);
  
  const [selectedRepairItems, setSelectedRepairItems] = useState([]);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedBikeId, setSelectedBikeId] = useState(null);
  const [bikeMaintenanceHistory, setBikeMaintenanceHistory] = useState([]);
  const [estimateDetailDialogOpen, setEstimateDetailDialogOpen] = useState(false);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [useAutoFill, setUseAutoFill] = useState(false);
  
  // Technician management
  const [technicians, setTechnicians] = useState([]);
  const [userNames, setUserNames] = useState([]); // System users as technician options
  const [technicianDialogOpen, setTechnicianDialogOpen] = useState(false);
  const [technicianFormData, setTechnicianFormData] = useState({ name: '', mobile: '', specialization: '', location: '' });
  const [floorScreenOpen, setFloorScreenOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [floorLocationFilter, setFloorLocationFilter] = useState('all');
  const [swapLocations, setSwapLocations] = useState([]);  // Locations from Manage Locations
  
  // Admin Discount state
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [discountRecord, setDiscountRecord] = useState(null);
  const [discountForm, setDiscountForm] = useState({ amount: '', reason: '', other_reason: '' });

  // Bulk selection state
  const [selectedRecords, setSelectedRecords] = useState([]);

  const toggleSelectRecord = (id) => {
    setSelectedRecords(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedRecords.length === filteredMaintenanceRecords.length) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredMaintenanceRecords.map(r => r.id));
    }
  };

  const handleBulkPaymentStatus = async (newStatus) => {
    if (selectedRecords.length === 0) {
      toast.error('Please select at least one record');
      return;
    }
    try {
      await axios.patch(`${API}/maintenance/bulk-payment-status`, {
        record_ids: selectedRecords,
        payment_status: newStatus
      }, { headers: getAuthHeader() });
      const label = newStatus === 'recovered_from_salary' ? 'Recovered from Salary' : newStatus.charAt(0).toUpperCase() + newStatus.slice(1);
      toast.success(`${selectedRecords.length} record(s) marked as ${label}`);
      setSelectedRecords([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };
  
  // Apply changes confirmation dialog
  const [applyChangesDialogOpen, setApplyChangesDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    bike_id: '',
    bike_plate_number: '',
    rider_id: '',
    rider_name: '',
    rider_emirates_id: '',
    emirates_id_photo_url: '',
    maintenance_date: '',
    maintenance_time: '',
    technician: '',
    type: 'routine',
    police_report_type: '',  // green, red, not_available (for accident type)
    police_report_url: '',   // URL of uploaded police report
    repair_items: [],
    total_cost: 0,
    estimate_amount: 0,
    cost_remarks: '',  // Remarks like "Under AMC", "Green Police Report"
    status: 'scheduled',
    notes: '',
    odometer_km: '',
    pre_maintenance_photos: [],
    post_maintenance_photos: [],
    location: ''
  });

  const [repairItemForm, setRepairItemForm] = useState({
    item_code: '',
    item_name: '',
    category: 'general',
    description: '',
    rate: 0,
    amc_type: 'non_amc',
    repair_type: 'labour_only'
  });

  // Set default location when user data is available
  useEffect(() => {
    if (user?.default_location && !editingId) {
      setFormData(prev => ({
        ...prev,
        location: prev.location || user.default_location
      }));
    }
  }, [user?.default_location, editingId]);

  useEffect(() => {
    fetchData();
    fetchTechnicians();
  }, []);

  useEffect(() => {
    // Apply filters when URL params or maintenance data changes
    const filter = searchParams.get('filter');
    setActiveFilter(filter);
    applyFilter(filter);
  }, [searchParams, maintenanceRecords]);

  useEffect(() => {
    // Normalize maintenance records for filtering (ensure date field exists)
    const normalizedRecords = maintenanceRecords.map(record => ({
      ...record,
      date: record.maintenance_date // Add date field for filterData function
    }));
    
    // Apply new filter pattern
    let filtered = filterData(normalizedRecords, filters);

    // Repair-item filter — filterData doesn't know about the nested
    // repair_items[] array, so we handle it here. Matches by
    // repair_item_id; falls back to item_name (case-insensitive) for
    // legacy records missing the id linkage.
    if (filters.repairItem) {
      const target = filters.repairItem;
      const targetName = (repairItems.find(i => i.id === target)?.item_name || '').toLowerCase();
      filtered = filtered.filter(record => {
        const items = record.repair_items || [];
        return items.some(ri => {
          const rid = ri.repair_item_id || ri.id;
          if (rid && rid === target) return true;
          if (targetName && (ri.item_name || '').toLowerCase() === targetName) return true;
          return false;
        });
      });
    }
    
    // Apply sorting
    const sorted = sortData(filtered, sortConfig.field, sortConfig.direction);
    setFilteredMaintenanceRecords(sorted);
  }, [maintenanceRecords, filters, sortConfig, repairItems]);

  // Trigger search manually (button click or Enter key)
  const handleSearch = () => {
    setActiveSearch(searchQuery);
    setCurrentPage(1);
    fetchData(1, searchQuery);
  };

  // When search text is cleared (backspace/delete), auto-reset to original state
  useEffect(() => {
    if (searchQuery === '' && activeSearch !== '') {
      setActiveSearch('');
      setCurrentPage(1);
      fetchData(1, '');
    }
  }, [searchQuery]);

  const handleClearSearch = () => {
    setSearchQuery('');
    setActiveSearch('');
    setCurrentPage(1);
    fetchData(1, '');
  };

  // Auto-refresh for Floor Screen
  useEffect(() => {
    if (!floorScreenOpen) return;
    
    // Update clock every second
    const clockInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    // Refresh data every 30 seconds
    const dataInterval = setInterval(() => {
      fetchData();
    }, 30000);
    
    return () => {
      clearInterval(clockInterval);
      clearInterval(dataInterval);
    };
  }, [floorScreenOpen]);

  const applyFilter = (filter) => {
    if (!filter) {
      setFilteredMaintenanceRecords(maintenanceRecords);
      return;
    }

    let filtered = [];
    switch (filter) {
      case 'scheduled':
        filtered = maintenanceRecords.filter(record => record.status === 'scheduled');
        break;
      case 'in-progress':
        filtered = maintenanceRecords.filter(record => record.status === 'in-progress');
        break;
      case 'completed':
        filtered = maintenanceRecords.filter(record => record.status === 'completed');
        break;
      default:
        filtered = maintenanceRecords;
    }
    setFilteredMaintenanceRecords(filtered);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const clearFilter = () => {
    setSearchParams({});
    setActiveFilter(null);
    setSearchQuery('');
    setActiveSearch('');
    setFilters({ fromDate: '', toDate: '', lessee: '', rider: '', bike: '', technician: '' });
  };

  const fetchData = async (page = currentPage, search = activeSearch) => {
    try {
      setLoading(true);
      if (search) setIsSearching(true);
      const skip = (page - 1) * ITEMS_PER_PAGE;
      
      const params = new URLSearchParams();
      params.append('limit', ITEMS_PER_PAGE);
      params.append('skip', skip);
      if (search) params.append('search', search);
      
      // Fetch all data with individual error handling
      const results = await Promise.allSettled([
        axios.get(`${API}/maintenance?${params.toString()}`, { headers: getAuthHeader() }),
        axios.get(`${API}/maintenance/count?${search ? `search=${encodeURIComponent(search)}` : ''}`, { headers: getAuthHeader() }),
        axios.get(`${API}/maintenance/stats`, { headers: getAuthHeader() }),
        axios.get(`${API}/repair-items`, { headers: getAuthHeader() }),
        axios.get(`${API}/motorcycles/dropdown`, { headers: getAuthHeader() }),
        axios.get(`${API}/riders/dropdown`, { headers: getAuthHeader() }),
        axios.get(`${API}/lessees`, { headers: getAuthHeader() }),
        axios.get(`${API}/spares`, { headers: getAuthHeader() }),
        axios.get(`${API}/swap-locations`, { headers: getAuthHeader() })
      ]);
      
      const [maintenanceRes, countRes, statsRes, repairItemsRes, bikesRes, ridersRes, lesseesRes, sparesRes, locationsRes] = results;
      
      setMaintenanceRecords(maintenanceRes.status === 'fulfilled' ? maintenanceRes.value.data : []);
      setTotalCount(countRes.status === 'fulfilled' ? countRes.value.data.count : 0);
      setStats(statsRes.status === 'fulfilled' ? statsRes.value.data : {});
      setRepairItems(repairItemsRes.status === 'fulfilled' ? repairItemsRes.value.data : []);
      setMotorcycles(bikesRes.status === 'fulfilled' ? bikesRes.value.data : []);
      setRiders(ridersRes.status === 'fulfilled' ? ridersRes.value.data : []);
      setLessees(lesseesRes.status === 'fulfilled' ? lesseesRes.value.data : []);
      setSpares(sparesRes.status === 'fulfilled' ? sparesRes.value.data : []);
      setSwapLocations(locationsRes.status === 'fulfilled' ? locationsRes.value.data : []);
      
      // Log any failures for debugging
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const endpoints = ['maintenance', 'maintenance/count', 'maintenance/stats', 'repair-items', 'motorcycles', 'riders', 'lessees', 'spares', 'swap-locations'];
          console.error(`Failed to fetch ${endpoints[index]}:`, result.reason?.message || result.reason);
        }
      });
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  const [talentStaff, setTalentStaff] = useState([]); // Staff from Talent PMS

  const fetchTechnicians = async () => {
    try {
      // Fetch dedicated technicians (legacy)
      const response = await axios.get(`${API}/technicians`, { headers: getAuthHeader() });
      setTechnicians(response.data);
      
      // Fetch staff from Talent PMS
      try {
        const staffRes = await axios.get(`${API}/talent/employees?employee_type=staff`, { headers: getAuthHeader() });
        // API returns { employees: [...], summary: {...} }
        const employees = staffRes.data?.employees || staffRes.data || [];
        setTalentStaff(Array.isArray(employees) ? employees : []);
      } catch (staffError) {
        console.log('Could not fetch Talent staff:', staffError);
        setTalentStaff([]);
      }
    } catch (error) {
      console.error('Failed to fetch technicians:', error);
      // Don't show error toast as this is not critical
    }
  };

  const handleAddTechnician = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/technicians`, technicianFormData, { headers: getAuthHeader() });
      toast.success('Technician added successfully');
      setTechnicianDialogOpen(false);
      setTechnicianFormData({ name: '', mobile: '', specialization: '', location: '' });
      fetchTechnicians();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to add technician'));
    }
  };

  const handleDeleteTechnician = async (technicianId) => {
    if (!window.confirm('Are you sure you want to delete this technician?')) {
      return;
    }
    try {
      await axios.delete(`${API}/technicians/${technicianId}`, { headers: getAuthHeader() });
      toast.success('Technician deleted successfully');
      fetchTechnicians();
    } catch (error) {
      toast.error('Failed to delete technician');
    }
  };

  const handleBikeSelect = (bikeId) => {
    const bike = motorcycles.find(b => b.id === bikeId);
    if (bike) {
      // Check if the motorcycle has an assigned rider
      let riderData = {
        bike_id: bikeId,
        bike_plate_number: bike.plate_number,
        bike_golden_plate_number: bike.golden_plate_number || ''
      };
      
      // If motorcycle has assigned rider, auto-fill rider information
      if (bike.assigned_rider_id || bike.rider_id) {
        const assignedRiderId = bike.assigned_rider_id || bike.rider_id;
        const assignedRider = riders.find(r => r.id === assignedRiderId);
        
        if (assignedRider) {
          riderData.rider_id = assignedRider.id;
          riderData.rider_name = assignedRider.name;
          riderData.rider_emirates_id = assignedRider.emirates_id || '';
          toast.success(`Auto-filled rider: ${assignedRider.name}`);
        }
      }
      
      setFormData({
        ...formData,
        ...riderData
      });
    }
  };

  const handleRiderSelect = (riderId) => {
    const rider = riders.find(r => r.id === riderId);
    if (rider) {
      setFormData({
        ...formData,
        rider_id: riderId,
        rider_name: rider.name,
        rider_emirates_id: rider.emirates_id || ''
      });
    }
  };

  const handleAddRepairItem = (repairItem) => {
    const existing = selectedRepairItems.find(item => item.repair_item_id === repairItem.id);
    if (existing) {
      toast.error('Item already added');
      return;
    }
    
    const newItem = {
      repair_item_id: repairItem.id,
      item_code: repairItem.item_code,
      item_name: repairItem.item_name || repairItem.description, // Fallback to description
      description: repairItem.description,
      rate: repairItem.rate,
      quantity: 1
    };
    
    const updatedItems = [...selectedRepairItems, newItem];
    setSelectedRepairItems(updatedItems);
    calculateTotalCost(updatedItems);
  };

  const handleRemoveRepairItem = (repairItemId) => {
    const updatedItems = selectedRepairItems.filter(item => item.repair_item_id !== repairItemId);
    setSelectedRepairItems(updatedItems);
    calculateTotalCost(updatedItems);
  };

  const handleQuantityChange = (repairItemId, quantity) => {
    const updatedItems = selectedRepairItems.map(item => 
      item.repair_item_id === repairItemId ? { ...item, quantity: parseInt(quantity) || 1 } : item
    );
    setSelectedRepairItems(updatedItems);
    calculateTotalCost(updatedItems);
  };

  const calculateTotalCost = (items) => {
    const total = items.reduce((sum, item) => sum + (item.rate * item.quantity), 0);
    setFormData(prev => ({ ...prev, total_cost: total, estimate_amount: total, repair_items: items }));
  };

  const printEstimate = (maintenance) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Maintenance Estimate - ${maintenance.bike_plate_number}</title>
          <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap" rel="stylesheet">
          <style>
            body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; padding: 20px; font-size: 14px; }
            .header { text-align: center; margin-bottom: 30px; }
            .logo { font-family: 'Space Grotesk', sans-serif; font-size: 24px; font-weight: bold; color: #8BC34A; }
            .info { margin: 20px 0; }
            .info-row { display: flex; margin: 10px 0; }
            .label { font-weight: 600; width: 150px; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #8BC34A; color: white; font-weight: 600; font-size: 12px; text-transform: uppercase; }
            .total { font-family: 'Space Grotesk', sans-serif; font-size: 18px; font-weight: bold; text-align: right; margin-top: 20px; }
            @media print { button { display: none; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">EZY MOBILITY</div>
            <h2>Maintenance Estimate</h2>
          </div>
          <div class="info">
            <div class="info-row"><span class="label">Plate Number:</span><span>${maintenance.bike_plate_number}</span></div>
            <div class="info-row"><span class="label">Date:</span><span>${formatDateDisplay(maintenance.maintenance_date)}</span></div>
            <div class="info-row"><span class="label">Technician:</span><span>${maintenance.technician}</span></div>
            <div class="info-row"><span class="label">Rider:</span><span>${maintenance.rider_name}</span></div>
            <div class="info-row"><span class="label">Emirates ID:</span><span>${maintenance.rider_emirates_id}</span></div>
          </div>
          <h3>Repair Items:</h3>
          <table>
            <thead>
              <tr><th>Item</th><th>Category</th><th>Rate (Dh)</th><th>Quantity</th><th>Total (Dh)</th></tr>
            </thead>
            <tbody>
              ${maintenance.repair_items?.map(item => `
                <tr>
                  <td>${item.item_name}</td>
                  <td>${item.category}</td>
                  <td>${item.rate.toFixed(2)}</td>
                  <td>${item.quantity}</td>
                  <td>${(item.rate * item.quantity).toFixed(2)}</td>
                </tr>
              `).join('') || '<tr><td colspan="5">No items</td></tr>'}
            </tbody>
          </table>
          <div class="total">
            <p>Estimate: <CurrencyDisplay amount={maintenance.estimate_amount || 0} /></p>
            <p>Total Cost: <CurrencyDisplay amount={maintenance.total_cost || 0} /></p>
          </div>
          <div style="margin-top: 50px; text-align: center;">
            <button onclick="window.print()" style="padding: 10px 20px; background: #8BC34A; color: white; border: none; cursor: pointer; font-size: 16px;">Print</button>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleSpareRequest = async () => {
    if (!editingId) {
      toast.error('Please save maintenance record first');
      return;
    }
    try {
      const requestData = {
        ...spareRequestData,
        maintenance_id: editingId,
        requested_by: user?.name || 'Technician',
        requested_by_id: user?.id || ''
      };
      await axios.post(`${API}/spare-requests`, requestData, { headers: getAuthHeader() });
      toast.success('Spare requested successfully');
      setSpareRequestDialogOpen(false);
      resetSpareRequest();
    } catch (error) {
      toast.error('Failed to request spare');
    }
  };

  const handleSpareChange = (spareId) => {
    const spare = spares.find(s => s.id === spareId);
    if (spare) {
      setSpareRequestData({
        ...spareRequestData,
        spare_id: spareId,
        spare_name: spare.part_name,
        part_number: spare.part_number
      });
    }
  };

  const resetSpareRequest = () => {
    setSpareRequestData({
      spare_id: '',
      spare_name: '',
      part_number: '',
      quantity_requested: 1,
      notes: ''
    });
  };

  const handleSubmit = async (e, opts = {}) => {
    if (e && e.preventDefault) e.preventDefault();
    const { forceAllowDuplicate = false } = opts;
    
    // Validate required location field
    if (!formData.location) {
      toast.error('Please select a location');
      return;
    }
    
    try {
      if (editingId) {
        await axios.put(`${API}/maintenance/${editingId}`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Maintenance updated successfully');
      } else {
        const url = forceAllowDuplicate
          ? `${API}/maintenance?force_allow_duplicate=true`
          : `${API}/maintenance`;
        await axios.post(url, formData, {
          headers: getAuthHeader()
        });
        toast.success('Maintenance record added successfully');
      }
      // Delay dialog close to allow toast to render and data to be fetched
      setTimeout(() => {
        setDialogOpen(false);
        resetForm();
        fetchData();
      }, 100);
    } catch (error) {
      // Same-day duplicate — backend returns 409 with a machine-readable
      // detail. Ask the technician to confirm before overriding.
      const detail = error?.response?.data?.detail;
      const isDup = error?.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'duplicate_maintenance_items';
      if (isDup && !forceAllowDuplicate) {
        const rows = (detail.duplicates || []).slice(0, 5)
          .map(d => `• ${d.item_name || d.repair_item_id}${d.quantity && d.quantity > 1 ? ` ×${d.quantity}` : ''}${d.prior_technician ? ` (by ${d.prior_technician})` : ''}`)
          .join('\n');
        const more = detail.duplicates.length > 5 ? `\n…and ${detail.duplicates.length - 5} more` : '';
        const msg = `⚠️  This bike (${detail.bike_plate}) has already been recorded with these same repair item(s) today (${detail.maintenance_date}):\n\n${rows}${more}\n\nAre you sure you want to add another entry? Click OK to save anyway, or Cancel to review.`;
        if (window.confirm(msg)) {
          return handleSubmit(null, { forceAllowDuplicate: true });
        }
        toast.info('Entry cancelled — no duplicate created.');
        return;
      }
      toast.error(formatErrorMessage(error, 'Operation failed'));
    }
  };

  const handleEdit = (maintenance) => {
    setEditingId(maintenance.id);
    setFormData({
      bike_id: maintenance.bike_id || maintenance.motorcycle_id,
      bike_plate_number: maintenance.bike_plate_number || maintenance.motorcycle_plate,
      rider_id: maintenance.rider_id || '',
      rider_name: maintenance.rider_name || '',
      rider_emirates_id: maintenance.rider_emirates_id || '',
      emirates_id_photo_url: maintenance.emirates_id_photo_url || '',
      maintenance_date: maintenance.maintenance_date,
      maintenance_time: maintenance.maintenance_time,
      technician: maintenance.technician || maintenance.technician_name,
      type: maintenance.type || maintenance.maintenance_type,
      repair_items: maintenance.repair_items || [],
      total_cost: maintenance.total_cost,
      estimate_amount: maintenance.estimate_amount || 0,
      pre_maintenance_photos: maintenance.pre_maintenance_photos || [],
      post_maintenance_photos: maintenance.post_maintenance_photos || [],
      status: maintenance.status,
      notes: maintenance.notes || '',
      odometer_km: maintenance.odometer_km || '',
      location: maintenance.location || user?.default_location || ''
    });
    setSelectedRepairItems(maintenance.repair_items || []);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this maintenance record?')) {
      try {
        await axios.delete(`${API}/maintenance/${id}`, {
          headers: getAuthHeader()
        });
        toast.success('Maintenance deleted successfully');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete maintenance');
      }
    }
  };

  const resetForm = () => {
    // Use user's default location if set
    const defaultLocation = user?.default_location || '';
    setFormData({
      bike_id: '', bike_plate_number: '', rider_id: '', rider_name: '', rider_emirates_id: '', emirates_id_photo_url: '',
      maintenance_date: '', maintenance_time: '', technician: '', type: 'routine',
      repair_items: [], total_cost: 0, estimate_amount: 0, status: 'scheduled', notes: '',
      pre_maintenance_photos: [], post_maintenance_photos: [],
      location: defaultLocation
    });
    setSelectedRepairItems([]);
    setEditingId(null);
  };

  // Repair Item Management
  const handleRepairItemSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRepairItemId) {
        // Save the item first (applies to new entries)
        await axios.put(`${API}/repair-items/${editingRepairItemId}`, repairItemForm, {
          headers: getAuthHeader()
        });
        setRepairItems(prev => prev.map(item => 
          item.id === editingRepairItemId 
            ? { ...item, ...repairItemForm }
            : item
        ));
        toast.success('Repair item updated');
        setRepairItemDialogOpen(false);
        resetRepairItemForm();
        // Ask if changes should apply to existing maintenance entries
        setApplyChangesDialogOpen(true);
        await fetchData();
      } else {
        const response = await axios.post(`${API}/repair-items`, repairItemForm, {
          headers: getAuthHeader()
        });
        setRepairItems(prev => [...prev, response.data]);
        toast.success('Repair item added');
        setRepairItemDialogOpen(false);
        resetRepairItemForm();
        await fetchData();
      }
    } catch (error) {
      toast.error('Operation failed');
      // Refresh data to revert any optimistic updates on error
      await fetchData();
    }
  };

  const handleEditRepairItem = (item) => {
    setEditingRepairItemId(item.id);
    setRepairItemForm({
      item_code: item.item_code || '',
      item_name: item.item_name || '',
      category: item.category || 'general',
      description: item.description || '',
      rate: item.rate || 0,
      amc_type: item.amc_type || 'non_amc',
      repair_type: item.repair_type || 'labour_only'
    });
    setRepairItemDialogOpen(true);
  };

  const handleDeleteRepairItem = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        await axios.delete(`${API}/repair-items/${id}`, { headers: getAuthHeader() });
        toast.success('Repair item deleted');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  const resetRepairItemForm = () => {
    setRepairItemForm({ item_code: '', item_name: '', category: 'general', description: '', rate: 0, amc_type: 'non_amc', repair_type: 'labour_only' });
    setEditingRepairItemId(null);
  };

  const handleBulkUploadRepairItems = async (data) => {
    try {
      // Validate required fields before sending
      const validationErrors = [];
      data.forEach((item, idx) => {
        if (!item.item_code) validationErrors.push(`Row ${idx + 2}: item_code is required`);
        if (!item.category) validationErrors.push(`Row ${idx + 2}: category is required`);
        if (!item.description) validationErrors.push(`Row ${idx + 2}: description is required`);
      });
      
      if (validationErrors.length > 0) {
        toast.error(`Validation errors:\n${validationErrors.slice(0, 5).join('\n')}${validationErrors.length > 5 ? `\n...and ${validationErrors.length - 5} more` : ''}`);
        return;
      }
      
      const response = await axios.post(
        `${API}/repair-items/bulk`,
        { repair_items: data },
        { headers: getAuthHeader() }
      );
      
      if (response.data.success_count > 0) {
        toast.success(`Successfully uploaded ${response.data.success_count} repair items`);
      }
      if (response.data.failed_count > 0) {
        const errorMsg = response.data.errors?.join('\n') || `${response.data.failed_count} items failed`;
        toast.error(`Upload errors:\n${errorMsg}`);
      }
      if (response.data.success_count > 0) {
        fetchData();
        setBulkUploadRepairItemsOpen(false);
      }
    } catch (error) {
      console.error('Bulk upload error:', error);
      const errorDetail = error.response?.data?.detail || error.message || 'Bulk upload failed';
      toast.error(errorDetail);
    }
  };

  const handleViewBikeHistory = async (bikeId, bikePlate) => {
    try {
      setSelectedBikeId(bikeId);
      const response = await axios.get(`${API}/maintenance?bike_id=${bikeId}`, {
        headers: getAuthHeader()
      });
      setBikeMaintenanceHistory(response.data);
      setHistoryDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load maintenance history');
    }
  };

  const handleViewEstimateDetail = (estimate) => {
    setSelectedEstimate(estimate);
    setEstimateDetailDialogOpen(true);
  };

  const closeHistoryDialog = () => {
    setHistoryDialogOpen(false);
    setSelectedBikeId(null);
    setBikeMaintenanceHistory([]);
  };

  const closeEstimateDialog = () => {
    setEstimateDetailDialogOpen(false);
    setSelectedEstimate(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-green-800',
      'in-progress': 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Admin Discount predefined reasons
  const DISCOUNT_REASONS = [
    'Factory defect, to be covered under warranty',
    'Assembly defect, to be covered by Company',
    'Defect due to non availability of spares',
    'Maintenance caused not attributed to Rider',
  ];

  const initDiscountForm = (record) => {
    const existing = record.admin_discount_reason || '';
    const isPredefined = DISCOUNT_REASONS.includes(existing);
    return {
      amount: record.admin_discount_amount || '',
      reason: isPredefined ? existing : (existing ? 'Other' : ''),
      other_reason: isPredefined ? '' : existing,
    };
  };

  // Admin Discount handler
  const handleApplyDiscount = async () => {
    const finalReason = discountForm.reason === 'Other' ? discountForm.other_reason : discountForm.reason;
    if (!discountRecord || !discountForm.amount || !finalReason) {
      toast.error('Please provide discount amount and reason');
      return;
    }
    try {
      const res = await axios.post(`${API}/maintenance/${discountRecord.id}/admin-discount`, {
        discount_amount: parseFloat(discountForm.amount),
        discount_reason: finalReason
      }, { headers: getAuthHeader() });
      toast.success(res.data.message || 'Admin discount applied');
      setDiscountDialogOpen(false);
      setDiscountRecord(null);
      setDiscountForm({ amount: '', reason: '', other_reason: '' });
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to apply discount'));
    }
  };

  // Rate list export functions
  const exportRateListExcel = () => {
    const XLSX = require('xlsx');
    const data = repairItems.map(item => ({
      'Item Code': item.item_code,
      'Category': item.category,
      'Description': item.description,
      'Rate (Dh)': item.rate,
      'AMC Type': item.amc_type === 'amc' ? 'AMC' : 'Non-AMC',
      'Type': item.repair_type === 'labour_spares' ? 'Labour + Spares' : item.repair_type === 'outside_fabrication' ? 'Outside Fabrication' : 'Labour Only'
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Rate List');
    // Auto-size columns
    ws['!cols'] = [{ wch: 12 }, { wch: 15 }, { wch: 30 }, { wch: 10 }, { wch: 12 }, { wch: 18 }];
    XLSX.writeFile(wb, `Repair_Rate_List_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Rate list exported to Excel');
  };

  const handleApplyToExisting = async () => {
    try {
      const res = await axios.post(`${API}/maintenance/recalculate-costs`, { dry_run: false }, { headers: getAuthHeader() });
      toast.success(`Updated ${res.data.changed} existing maintenance records`);
      setApplyChangesDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to update existing entries');
    }
  };

  const handleSummaryCardClick = async (type) => {
    setSummaryDetailLoading(true);
    setSummaryDetailOpen(true);
    try {
      const res = await axios.get(`${API}/maintenance/summary-details?type=${type}`, { headers: getAuthHeader() });
      setSummaryDetailData(res.data);
    } catch (err) {
      toast.error('Failed to load details');
    }
    setSummaryDetailLoading(false);
  };

  const exportRateListPDF = () => {
    const { jsPDF } = require('jspdf');
    require('jspdf-autotable');
    const doc = new jsPDF('l', 'mm', 'a4');
    doc.setFontSize(16);
    doc.text('Standard Repair Items & Rate List', 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 22);
    
    const rows = repairItems.map(item => [
      item.item_code,
      item.category,
      item.description,
      `${item.rate} Dh`,
      item.amc_type === 'amc' ? 'AMC' : 'Non-AMC',
      item.repair_type === 'labour_spares' ? 'Labour + Spares' : item.repair_type === 'outside_fabrication' ? 'Outside Fabrication' : 'Labour Only'
    ]);
    
    doc.autoTable({
      head: [['Item Code', 'Category', 'Description', 'Rate', 'AMC Type', 'Type']],
      body: rows,
      startY: 28,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [34, 90, 60] }
    });
    doc.save(`Repair_Rate_List_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Rate list exported to PDF');
  };

  const maintenanceDownloadColumns = [
    { header: 'Date', key: 'date', width: 12 },
    { header: 'Plate', key: 'bike_plate', width: 15 },
    { header: 'Rider', key: 'rider_name', width: 25 },
    { header: 'Category', key: 'category', width: 15 },
    { header: 'Description', key: 'description', width: 25 },
    { header: 'Vendor', key: 'vendor_name', width: 20 },
    { header: 'Amount', key: 'total_cost', width: 12, format: (v) => (v || 0).toFixed(2) },
    { header: 'Status', key: 'status', width: 12 },
  ];

  const handleDownloadMaintenance = async (format) => {
    try {
      toast.info(`Preparing ${format.toUpperCase()} download...`);
      // Fetch all matching records
      const params = new URLSearchParams();
      if (activeSearch) params.append('search', activeSearch);
      params.append('limit', '10000');
      params.append('skip', '0');
      
      const res = await axios.get(`${API}/maintenance?${params.toString()}`, { headers: getAuthHeader() });
      const allRecords = Array.isArray(res.data) ? res.data : (res.data.maintenance || []);
      
      const filterInfo = [
        activeSearch ? `Search: "${activeSearch}"` : '',
        activeFilter ? `Filter: ${activeFilter}` : '',
      ].filter(Boolean).join(' | ') || 'All records';
      
      if (format === 'excel') {
        downloadExcel(allRecords, maintenanceDownloadColumns, `Maintenance_Log_${new Date().toISOString().slice(0,10)}`, 'Maintenance');
      } else {
        downloadPDF(allRecords, maintenanceDownloadColumns, `Maintenance_Log_${new Date().toISOString().slice(0,10)}`, 'Maintenance Log', filterInfo);
      }
      toast.success(`${format.toUpperCase()} downloaded successfully`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error(`Failed to download ${format.toUpperCase()}`);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading...</div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="maintenance-page">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800">{t('maintenance.title')}</h1>
            <p className="text-sm sm:text-base text-slate-600 mt-1">{t('maintenance.subtitle')}</p>
            {activeFilter && (
              <div className="flex items-center gap-2 mt-2">
                <Badge className="bg-indigo-600 text-white">
                  {activeFilter === 'scheduled' && 'Scheduled'}
                  {activeFilter === 'in-progress' && 'In Progress'}
                  {activeFilter === 'completed' && 'Completed'}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilter}
                  className="h-6 px-2"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            )}
          </div>
          <TabsList className="flex-wrap">
            <TabsTrigger value="records" className="text-xs sm:text-sm">
              {isMobile ? 'Records' : 'Maintenance Records'}
            </TabsTrigger>
            {canViewRepairItems && (
              <TabsTrigger value="repair-items" className="text-xs sm:text-sm">
                {isMobile ? 'Items' : t('maintenance.repairItems')}
              </TabsTrigger>
            )}
          </TabsList>
        </div>

        <TabsContent value="records" className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card 
              className="cursor-pointer hover:shadow-md transition-all border-l-4 border-l-blue-500"
              onClick={() => handleSummaryCardClick('maintenance_cost')}
              data-testid="summary-maintenance-cost"
            >
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 font-medium">Total Maintenance Cost</p>
                <p className="text-sm text-slate-400">(Excluding AMC)</p>
                <p className="text-xl font-bold text-blue-600 mt-1"><CurrencyDisplay amount={stats.total_cost} /></p>
              </CardContent>
            </Card>
            <Card 
              className="cursor-pointer hover:shadow-md transition-all border-l-4 border-l-green-500"
              onClick={() => handleSummaryCardClick('amc_cost')}
              data-testid="summary-amc-cost"
            >
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 font-medium">Total AMC Cost</p>
                <p className="text-sm text-slate-400">(Free Items Value)</p>
                <p className="text-xl font-bold text-green-600 mt-1"><CurrencyDisplay amount={stats.total_amc_cost} /></p>
              </CardContent>
            </Card>
            <Card 
              className="cursor-pointer hover:shadow-md transition-all border-l-4 border-l-orange-500"
              onClick={() => handleSummaryCardClick('admin_discount')}
              data-testid="summary-admin-discount"
            >
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 font-medium">Total Admin Discounts</p>
                <p className="text-xl font-bold text-orange-600 mt-1"><CurrencyDisplay amount={stats.total_admin_discount} /></p>
              </CardContent>
            </Card>
            <Card 
              className="cursor-pointer hover:shadow-md transition-all border-l-4 border-l-red-500"
              onClick={() => handleSummaryCardClick('bike_ranking')}
              data-testid="summary-highest-bike"
            >
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 font-medium">Highest Cost Bike</p>
                <p className="text-sm text-slate-400">{stats.current_month || 'This Month'}</p>
                {stats.highest_bike_month ? (
                  <>
                    <p className="text-sm font-bold text-red-600 mt-1">{stats.highest_bike_month.plate}</p>
                    <p className="text-lg font-bold text-red-600"><CurrencyDisplay amount={stats.highest_bike_month.cost} /></p>
                  </>
                ) : (
                  <p className="text-sm text-slate-400 mt-1">No data this month</p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-end">
            {canCreate && (
              <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-lime-600 to-green-600" size={isMobile ? "sm" : "default"} data-testid="add-maintenance-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    {isMobile ? 'Add' : t('maintenance.addMaintenance')}
                  </Button>
                </DialogTrigger>
              <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-5xl'} max-h-[90vh] overflow-y-auto`}>
                <DialogHeader>
                  <DialogTitle>{editingId ? 'Update Maintenance Record' : 'Add New Maintenance'}</DialogTitle>
                  <DialogDescription>
                    {editingId 
                      ? 'Update status, add post-maintenance photos, and notes after work completion' 
                      : 'Record maintenance work with pre-maintenance photos and repair items'}
                  </DialogDescription>
                </DialogHeader>
                
                {/* Auto-Fill Toggle */}
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-blue-800">Smart Auto-Fill Mode</span>
                    <Badge variant="secondary" className="text-xs">NEW</Badge>
                  </div>
                  <Button
                    type="button"
                    variant={useAutoFill ? "default" : "outline"}
                    size="sm"
                    onClick={() => setUseAutoFill(!useAutoFill)}
                    className={useAutoFill ? "bg-blue-600 hover:bg-blue-700" : ""}
                  >
                    {useAutoFill ? 'Enabled' : 'Enable'}
                  </Button>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                    {/* Motorcycle Selection */}
                    {useAutoFill ? (
                      <AutoFillInput
                        label="Search Motorcycle by Plate *"
                        type="motorcycle"
                        searchBy="plate"
                        placeholder="Type plate number (e.g., DXB A 12345)"
                        required
                        onSelect={(motorcycle) => {
                          setFormData({
                            ...formData,
                            bike_id: motorcycle.id,
                            bike_plate_number: motorcycle.plate_number,
                            bike_golden_plate_number: motorcycle.golden_plate_number || ''
                          });
                        }}
                      />
                    ) : (
                      <div className="space-y-2">
                        <Label>Select Motorcycle *</Label>
                        <SearchableSelect
                          options={motorcycles.map((bike) => ({
                            value: bike.id,
                            label: bike.golden_plate_number ? `${bike.plate_number} / ${bike.golden_plate_number}` : bike.plate_number,
                            searchText: `${bike.plate_number} ${bike.golden_plate_number || ''} ${bike.model_name} ${bike.chassis_number || ''} ${bike.lessee_name || ''}`,
                            metadata: {
                              'Model': bike.model_name,
                              'Chassis': bike.chassis_number,
                              'City': bike.city || '-',
                              'Lessee': bike.lessee_name || '-'
                            }
                          }))}
                          value={formData.bike_id}
                          onValueChange={handleBikeSelect}
                          placeholder="Select motorcycle"
                          emptyMessage="No motorcycles found"
                        />
                        {formData.bike_plate_number && (
                          <div className="mt-1"><PlatePair oldPlate={formData.bike_plate_number} goldenPlate={formData.bike_golden_plate_number} /></div>
                        )}
                      </div>
                    )}

                    {/* Rider Selection */}
                    {useAutoFill ? (
                      <AutoFillInput
                        label="Search Rider by Name *"
                        type="rider"
                        searchBy="name"
                        placeholder="Type rider name (e.g., Ahmed Ali)"
                        required
                        onSelect={(rider) => {
                          setFormData({
                            ...formData,
                            rider_id: rider.id,
                            rider_name: rider.name,
                            rider_emirates_id: rider.emirates_id
                          });
                        }}
                      />
                    ) : (
                      <div className="space-y-2">
                        <Label>Select Rider *</Label>
                        <SearchableSelect
                          options={riders.map((rider) => ({
                            value: rider.id,
                            label: rider.name,
                            searchText: `${rider.name} ${rider.emirates_id} ${rider.mobile_uae || ''} ${rider.license_number || ''}`,
                            metadata: {
                              'Emirates ID': rider.emirates_id,
                              'Mobile': rider.mobile_uae,
                              'License': rider.license_number,
                              'Platform': rider.platform
                            }
                          }))}
                          value={formData.rider_id}
                          onValueChange={handleRiderSelect}
                          placeholder="Select rider"
                          emptyMessage="No riders found"
                        />
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Emirates ID Field */}
                    {useAutoFill ? (
                      <AutoFillInput
                        label="Search by Emirates ID *"
                        type="rider"
                        searchBy="emirates_id"
                        placeholder="Type Emirates ID (e.g., 784-1990-1234567-1)"
                        required
                        onSelect={(rider) => {
                          setFormData({
                            ...formData,
                            rider_id: rider.id,
                            rider_name: rider.name,
                            rider_emirates_id: rider.emirates_id
                          });
                        }}
                      />
                    ) : (
                      <div className="space-y-2">
                        <Label>Rider Emirates ID *</Label>
                        <Input
                          value={formData.rider_emirates_id}
                          onChange={(e) => setFormData({ ...formData, rider_emirates_id: e.target.value })}
                          placeholder="784-1234-1234567-1"
                          required
                          data-testid="maintenance-emirates-id"
                        />
                      </div>
                    )}
                    <FileUpload
                      label="Emirates ID Photo"
                      value={formData.emirates_id_photo_url}
                      onChange={(url) => setFormData({ ...formData, emirates_id_photo_url: url })}
                      accept="image/*"
                      required
                      API={API}
                      getAuthHeader={getAuthHeader}
                    />
                    <div className="space-y-2">
                      <Label>Maintenance Date *</Label>
                      <Input
                        type="date"
                        value={formData.maintenance_date}
                        onChange={(e) => setFormData({ ...formData, maintenance_date: e.target.value })}
                        required
                        data-testid="maintenance-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Maintenance Time *</Label>
                      <Input
                        type="time"
                        value={formData.maintenance_time}
                        onChange={(e) => setFormData({ ...formData, maintenance_time: e.target.value })}
                        required
                        data-testid="maintenance-time"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>ODO Reading (KM)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.odometer_km}
                        onChange={(e) => setFormData({ ...formData, odometer_km: e.target.value })}
                        placeholder="e.g. 12450"
                        data-testid="maintenance-odometer"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Technician Name *</Label>
                        {canViewTechnicians && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setTechnicianDialogOpen(true);
                            }}
                            className="h-6 px-2 text-xs"
                          >
                            <Settings className="h-3 w-3 mr-1" />
                            Manage
                          </Button>
                        )}
                      </div>
                      <SearchableSelect
                        options={[
                          // Staff from Talent PMS - only "Bike technician" designation (case-insensitive)
                          ...talentStaff
                            .filter(staff => {
                              const designation = (staff.designation || '').toLowerCase();
                              return designation.includes('bike technician') || designation.includes('technician');
                            })
                            .map((staff) => ({
                              value: staff.name,
                              label: staff.name,
                              searchText: `${staff.name} ${staff.designation || ''} ${staff.employee_unique_id || ''}`,
                              metadata: {
                                'ID': staff.employee_unique_id || 'N/A',
                                'Designation': staff.designation || 'Staff'
                              }
                            })),
                          // Dedicated technicians (legacy) as fallback
                          ...technicians.map((tech) => ({
                            value: tech.name,
                            label: tech.name,
                            searchText: `${tech.name} ${tech.specialization || ''} ${tech.mobile || ''}`,
                            metadata: {
                              'Specialization': tech.specialization || 'General',
                              'Mobile': tech.mobile || 'N/A'
                            }
                          }))
                        ]}
                        value={formData.technician}
                        onValueChange={(value) => setFormData({ ...formData, technician: value })}
                        placeholder="Select technician"
                        emptyMessage="No technicians found. Add staff with 'Bike technician' designation in Talent Management."
                        required
                        data-testid="maintenance-technician"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Type *</Label>
                      <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value, police_report_type: '', police_report_url: '' })}>
                        <SelectTrigger data-testid="maintenance-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="routine">{t('maintenance.typeRoutine')}</SelectItem>
                          <SelectItem value="repair">{t('maintenance.typeRepair')}</SelectItem>
                          <SelectItem value="inspection">{t('maintenance.typeInspection')}</SelectItem>
                          <SelectItem value="accident">Accident</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Police Report Fields - Only shown for Accident type */}
                    {formData.type === 'accident' && (
                      <>
                        <div className="space-y-2">
                          <Label>Police Report Type *</Label>
                          <Select 
                            value={formData.police_report_type} 
                            onValueChange={(value) => setFormData({ ...formData, police_report_type: value })}
                          >
                            <SelectTrigger data-testid="police-report-type">
                              <SelectValue placeholder="Select report type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="green">🟢 Green Report</SelectItem>
                              <SelectItem value="red">🔴 Red Report</SelectItem>
                              <SelectItem value="not_available">⚪ Not Available</SelectItem>
                            </SelectContent>
                          </Select>
                          {formData.police_report_type === 'green' && (
                            <p className="text-xs text-green-600 bg-green-50 p-2 rounded">
                              ✓ With Green Police Report, maintenance cost will be zero
                            </p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label>Police Report Document</Label>
                          <div className="flex items-center gap-2">
                            {formData.police_report_url ? (
                              <>
                                <Button 
                                  type="button"
                                  variant="outline" 
                                  size="sm" 
                                  className="text-blue-600"
                                  onClick={() => window.open(formData.police_report_url, '_blank')}
                                >
                                  <Eye className="w-4 h-4 mr-1" /> View
                                </Button>
                                <Button 
                                  type="button"
                                  variant="outline" 
                                  size="sm" 
                                  className="text-green-600"
                                  onClick={() => {
                                    const link = document.createElement('a');
                                    link.href = formData.police_report_url;
                                    link.download = 'Police_Report.pdf';
                                    link.target = '_blank';
                                    link.click();
                                  }}
                                >
                                  <Download className="w-4 h-4 mr-1" /> Download
                                </Button>
                              </>
                            ) : (
                              <span className="text-sm text-slate-500">No document uploaded</span>
                            )}
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept=".jpg,.jpeg,.png,.pdf,.webp"
                                className="hidden"
                                onChange={async (e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    try {
                                      const formDataUpload = new FormData();
                                      formDataUpload.append('file', file);
                                      formDataUpload.append('entity_type', 'maintenance');
                                      formDataUpload.append('document_type', 'police_report');
                                      const response = await axios.post(`${API}/upload-document`, formDataUpload, {
                                        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                                      });
                                      if (response.data.url) {
                                        setFormData(prev => ({ ...prev, police_report_url: response.data.url }));
                                        toast.success('Police report uploaded successfully');
                                      }
                                    } catch (error) {
                                      toast.error('Failed to upload police report');
                                    }
                                  }
                                }}
                              />
                              <Button type="button" variant="outline" size="sm" className="text-amber-600">
                                <Upload className="w-4 h-4 mr-1" /> {formData.police_report_url ? 'Re-upload' : 'Upload'}
                              </Button>
                            </label>
                          </div>
                        </div>
                      </>
                    )}
                    
                    <div className="space-y-2">
                      <Label>Location *</Label>
                      <Select value={formData.location} onValueChange={(value) => setFormData({ ...formData, location: value })} required>
                        <SelectTrigger data-testid="maintenance-location" className={!formData.location ? 'border-red-300' : ''}>
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                        <SelectContent>
                          {swapLocations.filter(loc => loc.is_active !== false).map(loc => (
                            <SelectItem key={loc.id} value={loc.name}>{loc.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!formData.location && (
                        <p className="text-xs text-red-500">Location is required</p>
                      )}
                    </div>
                  </div>

                  {/* Repair Items Selection */}
                  <div className="space-y-2">
                    <Label>Select Repair Item</Label>
                    <SearchableSelect
                      options={repairItems.map((item) => ({
                        value: item.id,
                        label: `${item.item_code || ''} - ${item.description}`,
                        searchText: `${item.item_code || ''} ${item.description} ${item.category} ${item.rate}`,
                        metadata: {
                          'Item Code': item.item_code || '-',
                          'Category': item.category,
                          'Rate': item.rate
                        }
                      }))}
                      value=""
                      onValueChange={(value) => {
                        const item = repairItems.find(i => i.id === value);
                        if (item) handleAddRepairItem(item);
                      }}
                      placeholder="Search repair items by code or description..."
                      emptyMessage="No repair items found"
                      showMetadata={true}
                    />
                  </div>

                  {/* Selected Repair Items */}
                  {selectedRepairItems.length > 0 && (
                    <div className="border rounded-lg p-4 space-y-2">
                      <Label>Selected Repair Items</Label>
                      {selectedRepairItems.map((item) => (
                        <div key={item.repair_item_id} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                          <div className="flex-1">
                            <div className="font-medium font-mono text-sm">{item.item_code}</div>
                            <div className="text-xs text-slate-600">{item.description || item.item_name}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-xs">Qty:</Label>
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleQuantityChange(item.repair_item_id, e.target.value)}
                              className="w-16"
                            />
                            <span className="text-sm w-24 text-right"><CurrencyDisplay amount={item.rate * item.quantity} /></span>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRemoveRepairItem(item.repair_item_id)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-between pt-2 border-t">
                        <div className="space-y-1">
                          <p className="text-sm text-slate-600">Estimate: <CurrencyDisplay amount={formData.estimate_amount} /></p>
                        </div>
                        <span className="text-lg font-bold text-green-600">
                          Total: <CurrencyDisplay amount={formData.total_cost} />
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Estimate Amount (Dh)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.estimate_amount}
                      readOnly
                      disabled
                      className="bg-slate-100 cursor-not-allowed"
                      data-testid="maintenance-estimate"
                    />
                    <p className="text-xs text-slate-500">Auto-calculated based on selected repair items</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Floor Status</Label>
                      <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                        <SelectTrigger data-testid="maintenance-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="scheduled">{t('maintenance.statusScheduled')}</SelectItem>
                          <SelectItem value="in-progress">{t('maintenance.statusInProgress')}</SelectItem>
                          <SelectItem value="completed">{t('maintenance.statusCompleted')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Payment Status</Label>
                      <Select value={formData.payment_status || 'unpaid'} onValueChange={(value) => setFormData({ ...formData, payment_status: value })}>
                        <SelectTrigger data-testid="maintenance-payment-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unpaid">Unpaid</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="recovered_from_salary">Recovered from Salary</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Additional notes..."
                      data-testid="maintenance-notes"
                    />
                  </div>

                  {/* Pre and Post Maintenance Photos */}
                  <div className={editingId ? "grid grid-cols-2 gap-4" : "grid grid-cols-1 gap-4"}>
                    {/* Pre-Maintenance Photos - Always shown */}
                    <div className="space-y-2">
                      <Label>Pre-Maintenance Photos</Label>
                      <p className="text-xs text-slate-500">Upload multiple photos before maintenance work</p>
                      
                      {/* Display existing photos */}
                      {formData.pre_maintenance_photos.length > 0 && (
                        <div className="space-y-2">
                          {formData.pre_maintenance_photos.map((photo, index) => (
                            <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                              <div className="flex items-center gap-2 flex-1">
                                <img 
                                  src={photo} 
                                  alt={`Pre-maintenance photo ${index + 1}`}
                                  className="w-12 h-12 object-cover rounded border"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                                <div className="flex items-center gap-1 text-xs text-slate-600" style={{display: 'none'}}>
                                  <FileImage className="w-3 h-3" />
                                  Image {index + 1}
                                </div>
                                <span className="text-xs text-slate-500">Photo {index + 1}</span>
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  const newPhotos = formData.pre_maintenance_photos.filter((_, i) => i !== index);
                                  setFormData({ ...formData, pre_maintenance_photos: newPhotos });
                                }}
                                className="text-red-600 hover:text-red-700"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Multi-file upload */}
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          className="hidden"
                          id="pre-photos-input"
                          onChange={async (e) => {
                            const files = Array.from(e.target.files);
                            if (files.length === 0) return;
                            
                            // For demo purposes, we'll simulate file upload URLs
                            // In a real app, you'd upload to a cloud service
                            const newPhotoUrls = files.map(file => URL.createObjectURL(file));
                            setFormData({ 
                              ...formData, 
                              pre_maintenance_photos: [...formData.pre_maintenance_photos, ...newPhotoUrls] 
                            });
                          }}
                        />
                        <label htmlFor="pre-photos-input" className="cursor-pointer">
                          <div className="space-y-2">
                            <Camera className="w-8 h-8 mx-auto text-slate-400" />
                            <div className="text-sm font-medium text-slate-600">
                              Click to upload multiple photos
                            </div>
                            <div className="text-xs text-slate-400">
                              Or drag and drop images here
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                    
                    {/* Post-Maintenance Photos - Only shown when editing */}
                    {editingId && (
                      <div className="space-y-2">
                        <Label>Post-Maintenance Photos</Label>
                        <p className="text-xs text-slate-500">Upload multiple photos after maintenance work</p>
                        
                        {/* Display existing photos */}
                        {formData.post_maintenance_photos.length > 0 && (
                          <div className="space-y-2">
                            {formData.post_maintenance_photos.map((photo, index) => (
                              <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                                <div className="flex items-center gap-2 flex-1">
                                  <img 
                                    src={photo} 
                                    alt={`Post-maintenance photo ${index + 1}`}
                                    className="w-12 h-12 object-cover rounded border"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                  <div className="flex items-center gap-1 text-xs text-slate-600" style={{display: 'none'}}>
                                    <FileImage className="w-3 h-3" />
                                    Image {index + 1}
                                  </div>
                                  <span className="text-xs text-slate-500">Photo {index + 1}</span>
                                </div>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    const newPhotos = formData.post_maintenance_photos.filter((_, i) => i !== index);
                                    setFormData({ ...formData, post_maintenance_photos: newPhotos });
                                  }}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Multi-file upload */}
                        <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            className="hidden"
                            id="post-photos-input"
                            onChange={async (e) => {
                              const files = Array.from(e.target.files);
                              if (files.length === 0) return;
                              
                              // For demo purposes, we'll simulate file upload URLs
                              // In a real app, you'd upload to a cloud service
                              const newPhotoUrls = files.map(file => URL.createObjectURL(file));
                              setFormData({ 
                                ...formData, 
                                post_maintenance_photos: [...formData.post_maintenance_photos, ...newPhotoUrls] 
                              });
                            }}
                          />
                          <label htmlFor="post-photos-input" className="cursor-pointer">
                            <div className="space-y-2">
                              <Camera className="w-8 h-8 mx-auto text-slate-400" />
                              <div className="text-sm font-medium text-slate-600">
                                Click to upload multiple photos
                              </div>
                              <div className="text-xs text-slate-400">
                                Or drag and drop images here
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 justify-between">
                    <div className="flex gap-2">
                      {editingId && user?.role === 'Repair Technician' && (
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => setSpareRequestDialogOpen(true)}
                          className="gap-2"
                        >
                          <Package className="w-4 h-4" />
                          Request Spare
                        </Button>
                      )}
                      {editingId && (
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => printEstimate(formData)}
                          className="gap-2"
                        >
                          <Printer className="w-4 h-4" />
                          Print Estimate
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-gradient-to-r from-lime-600 to-green-600" data-testid="submit-maintenance-btn">
                        {editingId ? 'Update' : 'Add'} Maintenance
                      </Button>
                    </div>
                  </div>
                </form>
              </DialogContent>
              </Dialog>
            )}
          </div>

          {/* Technician Management Dialog */}
          <Dialog open={technicianDialogOpen} onOpenChange={setTechnicianDialogOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Manage Technicians</DialogTitle>
                <DialogDescription>
                  Add and manage technicians who can be assigned to maintenance jobs.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-6">
                {/* Add Technician Form */}
                <form onSubmit={handleAddTechnician} className="space-y-4 p-4 bg-slate-50 rounded-lg">
                  <h3 className="font-semibold text-sm">Add New Technician</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input
                        value={technicianFormData.name}
                        onChange={(e) => setTechnicianFormData({ ...technicianFormData, name: e.target.value })}
                        placeholder="Technician name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Mobile</Label>
                      <Input
                        value={technicianFormData.mobile}
                        onChange={(e) => setTechnicianFormData({ ...technicianFormData, mobile: e.target.value })}
                        placeholder="Mobile number"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Specialization</Label>
                      <Input
                        value={technicianFormData.specialization}
                        onChange={(e) => setTechnicianFormData({ ...technicianFormData, specialization: e.target.value })}
                        placeholder="e.g., Electrical"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Location</Label>
                      <Input
                        value={technicianFormData.location}
                        onChange={(e) => setTechnicianFormData({ ...technicianFormData, location: e.target.value })}
                        placeholder="e.g., Dubai Workshop"
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Technician
                  </Button>
                </form>

                {/* Technicians List */}
                <div className="space-y-2">
                  <h3 className="font-semibold text-sm">Current Technicians</h3>
                  {technicians.length === 0 ? (
                    <p className="text-sm text-slate-500 py-4 text-center">No technicians added yet</p>
                  ) : (
                    <div className="border rounded-lg divide-y">
                      {technicians.map((tech) => (
                        <div key={tech.id} className="flex items-center justify-between p-3 hover:bg-slate-50">
                          <div className="flex-1">
                            <p className="font-medium">{tech.name}</p>
                            <div className="flex gap-4 text-xs text-slate-500 mt-1">
                              {tech.specialization && <span>🔧 {tech.specialization}</span>}
                              {tech.mobile && <span>📱 {tech.mobile}</span>}
                              {tech.location && <span>📍 {tech.location}</span>}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTechnician(tech.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Spare Request Dialog */}
          <Dialog open={spareRequestDialogOpen} onOpenChange={setSpareRequestDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Request Spare from Inventory</DialogTitle>
                <DialogDescription>Select spare part needed for this maintenance</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Spare Part *</Label>
                  <Select value={spareRequestData.spare_id} onValueChange={handleSpareChange} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select spare part" />
                    </SelectTrigger>
                    <SelectContent>
                      {spares.map((spare) => (
                        <SelectItem key={spare.id} value={spare.id}>
                          {spare.part_name} - {spare.part_number} (Available: {spare.quantity_available})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Quantity *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={spareRequestData.quantity_requested}
                    onChange={(e) => setSpareRequestData({ ...spareRequestData, quantity_requested: parseInt(e.target.value) })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={spareRequestData.notes}
                    onChange={(e) => setSpareRequestData({ ...spareRequestData, notes: e.target.value })}
                    placeholder="Additional notes for this request"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => { setSpareRequestDialogOpen(false); resetSpareRequest(); }}>
                    Cancel
                  </Button>
                  <Button onClick={handleSpareRequest} className="bg-gradient-to-r from-lime-600 to-green-600">
                    Submit Request
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <CardTitle>Maintenance Records ({filteredMaintenanceRecords.length}{totalCount > filteredMaintenanceRecords.length ? ` of ${totalCount}` : ''})</CardTitle>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setFloorScreenOpen(true)}
                    className="gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  >
                    <Settings className="w-4 h-4" />
                    {!isMobile && 'Maintenance Floor Dashboard'}
                    {isMobile && 'Floor'}
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setReportOpen(true)}
                    className="gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
                    data-testid="open-maintenance-report-btn"
                  >
                    <BadgePercent className="w-4 h-4" />
                    {!isMobile && 'Maintenance Report'}
                    {isMobile && 'Report'}
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-1" data-testid="download-maintenance-btn">
                        <Download className="w-4 h-4" />
                        {!isMobile && 'Download'}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleDownloadMaintenance('excel')} className="cursor-pointer">
                        <FileSpreadsheet className="mr-2 h-4 w-4 text-green-600" />
                        Download Excel
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDownloadMaintenance('pdf')} className="cursor-pointer">
                        <FileDown className="mr-2 h-4 w-4 text-red-600" />
                        Download PDF
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    className="gap-2"
                  >
                    <Filter className="w-4 h-4" />
                    {isMobile ? (showFilters ? 'Hide' : 'Filter') : (showFilters ? 'Hide Filters' : 'Show Filters')}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Server-Side Search Bar */}
              <div className="flex gap-2 mb-4" data-testid="maintenance-search-bar">
                <div className="relative flex-1">
                  {isSearching ? (
                    <Loader className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-500 w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  )}
                  <Input
                    type="text"
                    placeholder="Search maintenance (plate, rider, technician, type...)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                    className="pl-10"
                    data-testid="maintenance-search-input"
                  />
                  {searchQuery && (
                    <button 
                      onClick={handleClearSearch}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      data-testid="maintenance-search-clear"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <Button 
                  onClick={handleSearch}
                  disabled={isSearching}
                  className="bg-lime-600 hover:bg-lime-700 text-white px-4"
                  data-testid="maintenance-search-button"
                >
                  <Search className="w-4 h-4 mr-1" />
                  Search
                </Button>
              </div>
              
              {showFilters && (
                <div className="mb-6 p-4 bg-slate-50 rounded-lg space-y-4">
                  <DateRangeFilter
                    fromDate={filters.fromDate}
                    toDate={filters.toDate}
                    onFromDateChange={(val) => setFilters({ ...filters, fromDate: val })}
                    onToDateChange={(val) => setFilters({ ...filters, toDate: val })}
                  />
                  <SearchFilters
                    lessees={lessees}
                    riders={riders}
                    motorcycles={motorcycles}
                    technicians={technicians}
                    selectedLessee={filters.lessee}
                    selectedRider={filters.rider}
                    selectedBike={filters.bike}
                    selectedTechnician={filters.technician}
                    onLesseeChange={(val) => setFilters({ ...filters, lessee: val })}
                    onRiderChange={(val) => setFilters({ ...filters, rider: val })}
                    onBikeChange={(val) => setFilters({ ...filters, bike: val })}
                    onTechnicianChange={(val) => setFilters({ ...filters, technician: val })}
                    showTechnician={true}
                  />
                  {/* Repair Item filter — searchable dropdown of predefined
                      repair items. Matches records whose repair_items[]
                      contains the selected item. */}
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">
                      Repair Item
                    </label>
                    <Popover open={repairItemFilterOpen} onOpenChange={setRepairItemFilterOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          role="combobox"
                          aria-expanded={repairItemFilterOpen}
                          className="w-full sm:w-[320px] justify-between font-normal bg-white"
                          data-testid="filter-repair-item-trigger"
                        >
                          {filters.repairItem
                            ? (() => {
                                const sel = repairItems.find(i => i.id === filters.repairItem);
                                return sel
                                  ? `${sel.item_code ? sel.item_code + ' — ' : ''}${sel.item_name}`
                                  : 'Selected item';
                              })()
                            : 'All repair items…'}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[320px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search repair item…" data-testid="filter-repair-item-search" />
                          <CommandList>
                            <CommandEmpty>No repair items found.</CommandEmpty>
                            <CommandGroup>
                              <CommandItem
                                value="__all__"
                                onSelect={() => {
                                  setFilters({ ...filters, repairItem: '' });
                                  setRepairItemFilterOpen(false);
                                }}
                                data-testid="filter-repair-item-all"
                              >
                                <span className={filters.repairItem ? 'opacity-60' : 'font-semibold'}>All repair items</span>
                              </CommandItem>
                              {repairItems.map((item) => (
                                <CommandItem
                                  key={item.id}
                                  value={`${item.item_code || ''} ${item.item_name || ''}`}
                                  onSelect={() => {
                                    setFilters({ ...filters, repairItem: item.id });
                                    setRepairItemFilterOpen(false);
                                  }}
                                  data-testid={`filter-repair-item-${item.id}`}
                                >
                                  <span className="flex-1 truncate">
                                    <span className="text-slate-500 text-[11px] font-mono mr-2">{item.item_code || ''}</span>
                                    {item.item_name}
                                  </span>
                                  {filters.repairItem === item.id && (
                                    <span className="text-emerald-600 text-xs ml-2">✓</span>
                                  )}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setFilters({ fromDate: '', toDate: '', lessee: '', rider: '', bike: '', technician: '', repairItem: '' })}>
                      Clear Filters
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Mobile/Tablet Card View */}
              {(isMobile || isTablet) ? (
                <div className="space-y-3">
                  {filteredMaintenanceRecords.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                      <Wrench className="w-12 h-12 mb-3 text-slate-300" />
                      <p className="text-sm">{activeFilter ? 'No records match filter' : 'No maintenance records'}</p>
                    </div>
                  ) : (
                    filteredMaintenanceRecords.map((record) => (
                      <Card 
                        key={record.id} 
                        className="overflow-hidden active:scale-[0.99] transition-transform cursor-pointer"
                        onClick={() => handleViewEstimateDetail(record)}
                        data-testid={`maintenance-card-${record.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              {/* Header - Plate & Status */}
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <PlatePair oldPlate={record.bike_plate_number || record.motorcycle_plate} goldenPlate={record.bike_golden_plate_number} />
                                <Badge className={getStatusColor(record.status)}>
                                  {record.status || 'Pending'}
                                </Badge>
                                <Badge className={
                                  record.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                                  record.payment_status === 'recovered_from_salary' ? 'bg-blue-100 text-blue-800' :
                                  'bg-red-100 text-red-800'
                                }>
                                  {record.payment_status === 'recovered_from_salary' ? 'Recovered' : record.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                                </Badge>
                              </div>

                              {/* Service Type */}
                              <p className="text-sm font-medium text-blue-600 mb-2">
                                {record.type || record.maintenance_type || '-'}
                              </p>

                              {/* Details Grid */}
                              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                <div>
                                  <span className="text-slate-500 text-xs">Date:</span>
                                  <p className="text-slate-700">{formatDateDisplay(record.maintenance_date)}</p>
                                </div>
                                <div>
                                  <span className="text-slate-500 text-xs">Technician:</span>
                                  <p className="text-slate-700 truncate">{record.technician || record.technician_name || '-'}</p>
                                </div>
                                <div>
                                  <span className="text-slate-500 text-xs">Company:</span>
                                  <p className="text-slate-700 truncate">{record.lessee_name || '-'}</p>
                                </div>
                                <div>
                                  <span className="text-slate-500 text-xs">Total Cost:</span>
                                  <p className="text-emerald-600 font-bold">
                                    <CurrencyDisplay amount={record.total_cost} />
                                  </p>
                                </div>
                                <div>
                                  <span className="text-slate-500 text-xs">ODO (KM):</span>
                                  <p className="text-slate-700 font-mono">{record.odometer_km ? `${Number(record.odometer_km).toLocaleString()} km` : '-'}</p>
                                </div>
                              </div>

                              {/* Repair Items Count */}
                              <div className="mt-2 flex items-center gap-2">
                                <Package className="w-3 h-3 text-slate-400" />
                                <span className="text-xs text-slate-500">
                                  {record.repair_items?.length || 0}
                                </span>
                              </div>
                            </div>

                            {/* Actions */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-9 w-9 shrink-0"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={async (e) => { 
                                  e.stopPropagation(); 
                                  try {
                                    const motorcycle = motorcycles.find(m => m.id === record.bike_id);
                                    await generateMaintenancePDF(record, motorcycle);
                                    toast.success('PDF generated');
                                  } catch (error) {
                                    toast.error('PDF generation failed');
                                  }
                                }}>
                                  <Printer className="w-4 h-4 mr-2" />
                                  Download PDF
                                </DropdownMenuItem>
                                {canEdit && (
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEdit(record); }}>
                                    <Edit className="w-4 h-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                )}
                                {canEdit && (
                                  <DropdownMenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    setDiscountRecord(record);
                                    setDiscountForm(initDiscountForm(record));
                                    setDiscountDialogOpen(true);
                                  }}>
                                    <BadgePercent className="w-4 h-4 mr-2" />
                                    Admin Discount
                                  </DropdownMenuItem>
                                )}
                                {canDelete && (
                                  <DropdownMenuItem 
                                    onClick={(e) => { e.stopPropagation(); handleDelete(record.id); }} 
                                    className="text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              ) : (
                /* Desktop Table View - Full Page */
                <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
                  {/* Bulk Action Bar */}
                  {selectedRecords.length > 0 && (
                    <div className="p-3 bg-blue-50 border-b border-blue-200 flex items-center justify-between" data-testid="bulk-action-bar">
                      <span className="text-sm text-blue-800 font-medium">
                        {selectedRecords.length} record(s) selected
                      </span>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleBulkPaymentStatus('paid')}
                          className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                          data-testid="bulk-mark-paid-btn"
                        >
                          Mark as Paid
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleBulkPaymentStatus('unpaid')}
                          className="bg-red-50 border-red-300 text-red-700 hover:bg-red-100"
                          data-testid="bulk-mark-unpaid-btn"
                        >
                          Mark as Unpaid
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleBulkPaymentStatus('recovered_from_salary')}
                          className="bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                          data-testid="bulk-mark-recovered-btn"
                        >
                          Recovered from Salary
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSelectedRecords([])}
                          className="text-slate-600"
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                  )}
                  <div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 border-b border-slate-200">
                      <TableHead className="w-10">
                        <input
                          type="checkbox"
                          className="rounded border-slate-300 cursor-pointer"
                          checked={filteredMaintenanceRecords.length > 0 && selectedRecords.length === filteredMaintenanceRecords.length}
                          onChange={toggleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </TableHead>
                      <TableHead className="font-semibold text-slate-700">
                        <div className="flex items-center gap-1">
                          Date & Time
                          <SortButton onSort={(field, dir) => setSortConfig({field, direction: dir})} currentSort={sortConfig} field="date" />
                        </div>
                      </TableHead>
                      <TableHead className="font-semibold text-slate-700">Motorcycle Plate</TableHead>
                      <TableHead className="font-semibold text-slate-700">Lessee Company</TableHead>
                      <TableHead className="font-semibold text-slate-700">Rider</TableHead>
                      <TableHead className="font-semibold text-slate-700">Type</TableHead>
                      <TableHead className="font-semibold text-slate-700">Technician</TableHead>
                      <TableHead className="font-semibold text-slate-700">Repair Items</TableHead>
                      <TableHead className="font-semibold text-slate-700">Total Cost (Dh)</TableHead>
                      <TableHead className="font-semibold text-slate-700">Floor Status</TableHead>
                      <TableHead className="font-semibold text-slate-700">Payment Status</TableHead>
                      <TableHead className="font-semibold text-slate-700">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMaintenanceRecords.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} className="text-center text-slate-500 py-12">
                          <div className="space-y-2">
                            <Wrench className="w-8 h-8 mx-auto text-slate-400" />
                            <div>{activeFilter ? 'No maintenance records match the current filter.' : 'No maintenance records found.'}</div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      // Data is already sorted and paginated from server
                      filteredMaintenanceRecords
                        .map((record, index) => (
                          <TableRow 
                            key={record.id} 
                            data-testid={`maintenance-row-${record.id}`}
                            className={`cursor-pointer hover:bg-blue-50 transition-colors duration-150 border-b border-slate-100 ${selectedRecords.includes(record.id) ? 'bg-blue-50' : ''}`}
                            onClick={() => handleViewEstimateDetail(record)}
                          >
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                className="rounded border-slate-300 cursor-pointer"
                                checked={selectedRecords.includes(record.id)}
                                onChange={() => toggleSelectRecord(record.id)}
                                data-testid={`select-record-${record.id}`}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-1 h-8 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full"></div>
                                <div>
                                  <div className="font-medium text-slate-800">{formatDateDisplay(record.maintenance_date)}</div>
                                  <div className="text-xs text-slate-500">{record.maintenance_time}</div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="font-medium text-slate-800"><PlatePair oldPlate={record.bike_plate_number || record.motorcycle_plate} goldenPlate={record.bike_golden_plate_number} compact /></TableCell>
                            <TableCell className="text-slate-600">{record.lessee_name || '-'}</TableCell>
                            <TableCell>{record.rider_name || '-'}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="bg-slate-50">{record.type || record.maintenance_type || '-'}</Badge>
                            </TableCell>
                            <TableCell>{record.technician || record.technician_name || '-'}</TableCell>
                            <TableCell>
                              <div className="text-xs bg-slate-100 px-2 py-1 rounded-full text-center">
                                {record.repair_items?.length || 0}
                              </div>
                            </TableCell>
                            <TableCell className="min-w-[140px]">
                              {(() => {
                                const items = record.repair_items || [];
                                const subtotal = items.reduce((s, i) => s + ((i.rate || 0) * (i.quantity || 1)), 0);
                                const amcDiscount = items.filter(i => i.amc_covered).reduce((s, i) => s + ((i.rate || 0) * (i.quantity || 1)), 0);
                                return (
                                  <div className="text-xs whitespace-nowrap">
                                    <div className="flex justify-between gap-2 text-slate-500"><span>Cost:</span><span className="font-medium text-slate-700">{subtotal} Dh</span></div>
                                    {amcDiscount > 0 && (
                                      <div className="flex justify-between gap-2 text-green-600"><span>AMC:</span><span className="font-semibold">-{amcDiscount} Dh</span></div>
                                    )}
                                    <div className="flex justify-between gap-2 font-bold text-emerald-600 border-t border-slate-200 mt-0.5 pt-0.5"><span>Final:</span><span>{record.total_cost} Dh</span></div>
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              <Badge className={getStatusColor(record.status)}>
                                {record.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={
                                record.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                                record.payment_status === 'recovered_from_salary' ? 'bg-blue-100 text-blue-800' :
                                'bg-red-100 text-red-800'
                              }>
                                {record.payment_status === 'recovered_from_salary' ? 'Recovered' : record.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`actions-menu-${record.id}`}>
                                      <MoreVertical className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        const motorcycle = motorcycles.find(m => m.id === record.bike_id);
                                        await generateMaintenancePDF(record, motorcycle);
                                        toast.success('PDF generated successfully');
                                      } catch (error) {
                                        toast.error('Failed to generate PDF');
                                      }
                                    }}>
                                      <Printer className="w-4 h-4 mr-2" />
                                      Download PDF
                                    </DropdownMenuItem>
                                    {canEdit && (
                                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEdit(record); }}>
                                        <Edit className="w-4 h-4 mr-2" />
                                        Edit
                                      </DropdownMenuItem>
                                    )}
                                    {canEdit && (
                                      <DropdownMenuItem onClick={(e) => {
                                        e.stopPropagation();
                                        setDiscountRecord(record);
                                        setDiscountForm(initDiscountForm(record));
                                        setDiscountDialogOpen(true);
                                      }}>
                                        <BadgePercent className="w-4 h-4 mr-2" />
                                        Admin Discount
                                      </DropdownMenuItem>
                                    )}
                                    {canDelete && (
                                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDelete(record.id); }} className="text-red-600">
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Delete
                                      </DropdownMenuItem>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
                </div>
                
                {/* Pagination Controls */}
                {totalCount > ITEMS_PER_PAGE && (
                  <div className="flex items-center justify-between pt-4 border-t mt-4">
                    <div className="text-sm text-slate-500">
                      Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of {totalCount} records
                    </div>
                    <div className="flex items-center gap-1">
                      {/* First page */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1 || loading}
                        className="px-2"
                      >
                        {'<<'}
                      </Button>
                      
                      {/* Previous page */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || loading}
                        className="px-2"
                      >
                        {'<'}
                      </Button>
                      
                      {/* Page numbers */}
                      {getPageNumbers(Math.ceil(totalCount / ITEMS_PER_PAGE)).map((page, index) => (
                        page === '...' ? (
                          <span key={`ellipsis-${index}`} className="px-2 text-slate-400">...</span>
                        ) : (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(page)}
                            disabled={loading}
                            className={`px-3 ${currentPage === page ? 'bg-green-600 hover:bg-green-700' : ''}`}
                          >
                            {page}
                          </Button>
                        )
                      ))}
                      
                      {/* Next page */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === Math.ceil(totalCount / ITEMS_PER_PAGE) || loading}
                        className="px-2"
                      >
                        {'>'}
                      </Button>
                      
                      {/* Last page */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(Math.ceil(totalCount / ITEMS_PER_PAGE))}
                        disabled={currentPage === Math.ceil(totalCount / ITEMS_PER_PAGE) || loading}
                        className="px-2"
                      >
                        {'>>'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Repair Items Tab Content - Only accessible if user has view permission */}
        {canViewRepairItems && (
        <TabsContent value="repair-items" className="space-y-4">
          <div className="flex justify-end gap-2">
            {canCreateRepairItems && (
              <Dialog open={repairItemDialogOpen} onOpenChange={(open) => { setRepairItemDialogOpen(open); if (!open) resetRepairItemForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-lime-600 to-green-600" data-testid="add-repair-item-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Repair Item
                  </Button>
                </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingRepairItemId ? 'Edit Repair Item' : 'Add New Repair Item'}</DialogTitle>
                  <DialogDescription>Standard repair items with rates</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleRepairItemSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Item Code *</Label>
                      <Input
                        value={repairItemForm.item_code}
                        onChange={(e) => setRepairItemForm({ ...repairItemForm, item_code: e.target.value.toUpperCase() })}
                        placeholder="e.g., BRK-001"
                        required
                        data-testid="repair-item-code"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Category *</Label>
                      <Select value={repairItemForm.category} onValueChange={(value) => setRepairItemForm({ ...repairItemForm, category: value })}>
                        <SelectTrigger data-testid="repair-item-category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="engine">Engine</SelectItem>
                          <SelectItem value="electrical">Electrical</SelectItem>
                          <SelectItem value="body">Body</SelectItem>
                          <SelectItem value="tire">Tire</SelectItem>
                          <SelectItem value="battery">Battery</SelectItem>
                          <SelectItem value="special_items">Special Items</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Description *</Label>
                    <Textarea
                      value={repairItemForm.description}
                      onChange={(e) => setRepairItemForm({ ...repairItemForm, description: e.target.value })}
                      placeholder={repairItemForm.category === 'special_items' ? 'Describe the non-standard repair item' : 'Brief description of the repair item'}
                      required
                      data-testid="repair-item-description"
                      rows={3}
                    />
                    {repairItemForm.category === 'special_items' && (
                      <p className="text-xs text-amber-600">
                        Special Items: For non-standard repairs, provide a detailed description and rate
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Rate (Dh) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={repairItemForm.rate}
                      onChange={(e) => setRepairItemForm({ ...repairItemForm, rate: parseFloat(e.target.value) })}
                      placeholder="0.00"
                      required
                      data-testid="repair-item-rate"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>AMC Type *</Label>
                    <Select value={repairItemForm.amc_type || 'non_amc'} onValueChange={(value) => setRepairItemForm({ ...repairItemForm, amc_type: value })}>
                      <SelectTrigger data-testid="repair-item-amc-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="amc">✓ AMC - Covered under Annual Maintenance Contract</SelectItem>
                        <SelectItem value="non_amc">✗ Non-AMC - Chargeable</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      {repairItemForm.amc_type === 'amc' 
                        ? '✓ This item will be FREE for lessees with AMC' 
                        : '✗ This item will be charged at the defined rate'}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Repair Type *</Label>
                    <Select value={repairItemForm.repair_type || 'labour_only'} onValueChange={(value) => setRepairItemForm({ ...repairItemForm, repair_type: value })}>
                      <SelectTrigger data-testid="repair-item-repair-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="labour_only">Labour Only</SelectItem>
                        <SelectItem value="labour_spares">Labour + Spares</SelectItem>
                        <SelectItem value="outside_fabrication">Outside Fabrication</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="outline" onClick={() => { setRepairItemDialogOpen(false); resetRepairItemForm(); }}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-gradient-to-r from-lime-600 to-green-600" data-testid="submit-repair-item-btn">
                      {editingRepairItemId ? 'Update' : 'Add'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
              </Dialog>
            )}
          </div>

          {/* Bulk Upload Repair Items Dialog */}
          <Dialog open={bulkUploadRepairItemsOpen} onOpenChange={setBulkUploadRepairItemsOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Bulk Upload Repair Items</DialogTitle>
                <DialogDescription>
                  Upload multiple repair items at once using an Excel file. Download the template first to see the required format.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      
                      try {
                        // Parse Excel file
                        const XLSX = await import('xlsx');
                        const data = await file.arrayBuffer();
                        const workbook = XLSX.read(data);
                        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                        const jsonData = XLSX.utils.sheet_to_json(worksheet);
                        
                        // Show original columns for debugging
                        if (jsonData.length > 0) {
                          const originalKeys = Object.keys(jsonData[0]);
                          console.log('Original Excel columns:', originalKeys);
                        }
                        
                        // Normalize keys to lowercase and remove spaces/special chars
                        const normalizedData = jsonData.map(row => {
                          const normalized = {};
                          Object.keys(row).forEach(key => {
                            // More robust key normalization
                            const normalizedKey = key.toLowerCase()
                              .replace(/\s+/g, '_')
                              .replace(/[^a-z0-9_]/g, '');
                            normalized[normalizedKey] = row[key];
                          });
                          return normalized;
                        });
                        
                        console.log('Parsed and normalized Excel data:', normalizedData);
                        
                        // Validate data
                        if (normalizedData.length === 0) {
                          throw new Error('Excel file is empty');
                        }
                        
                        // Check for required columns
                        const requiredCols = ['item_code', 'category', 'description'];
                        const foundCols = Object.keys(normalizedData[0] || {});
                        const missingCols = requiredCols.filter(col => !foundCols.includes(col));
                        
                        if (missingCols.length > 0) {
                          throw new Error(`Missing required columns: ${missingCols.join(', ')}. Found columns: ${foundCols.join(', ')}`);
                        }
                        
                        // Upload data
                        await handleBulkUploadRepairItems(normalizedData);
                      } catch (error) {
                        toast.error(error.message || 'Failed to upload file');
                        console.error('Upload error:', error);
                      }
                    }}
                    className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={downloadRepairItemTemplate} className="gap-2">
                    <Download className="w-4 h-4" />
                    Download Template
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Repair Items Section - Only show if user has permission */}
          {canViewRepairItems && (
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Standard Repair Items & Rates</CardTitle>
                  {canCreateRepairItems && (
                    <div className="flex gap-2 flex-wrap">
                      <Button variant="outline" size="sm" onClick={exportRateListExcel} className="gap-1" data-testid="export-ratelist-excel">
                        <FileSpreadsheet className="w-4 h-4" />
                        Export Excel
                      </Button>
                      <Button variant="outline" size="sm" onClick={exportRateListPDF} className="gap-1" data-testid="export-ratelist-pdf">
                        <FileDown className="w-4 h-4" />
                        Export PDF
                      </Button>
                      <Button
                        variant="outline"
                        onClick={downloadRepairItemTemplate}
                        className="gap-2"
                      >
                        <Download className="w-4 h-4" />
                        {t('motorcycles.downloadTemplate')}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setBulkUploadRepairItemsOpen(true)}
                        className="gap-2"
                      >
                        <Upload className="w-4 h-4" />
                        {t('motorcycles.bulkUpload')}
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {/* Search Bar */}
                <div className="mb-4 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by keyword, item code, category, AMC type, type..."
                    value={repairItemSearch}
                    onChange={(e) => setRepairItemSearch(e.target.value)}
                    className="pl-9 pr-9"
                    data-testid="repair-item-search"
                  />
                  {repairItemSearch && (
                    <button onClick={() => setRepairItemSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item Code</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Rate (Dh)</TableHead>
                        <TableHead>AMC Type</TableHead>
                        <TableHead>Type</TableHead>
                        {(canEditRepairItems || canDeleteRepairItems) && <TableHead>Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const q = repairItemSearch.toLowerCase().trim();
                        const repairTypeLabel = (t) => t === 'labour_spares' ? 'labour + spares' : t === 'outside_fabrication' ? 'outside fabrication' : 'labour only';
                        const filtered = q ? repairItems.filter(item =>
                          (item.item_code || '').toLowerCase().includes(q) ||
                          (item.item_name || '').toLowerCase().includes(q) ||
                          (item.category || '').toLowerCase().replace('_', ' ').includes(q) ||
                          (item.description || '').toLowerCase().includes(q) ||
                          (item.amc_type === 'amc' ? 'amc' : 'non-amc').includes(q) ||
                          repairTypeLabel(item.repair_type).includes(q)
                        ) : repairItems;
                        return filtered.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                            {q ? 'No repair items match your search.' : 'No repair items configured. Add standard repair items with rates.'}
                          </TableCell>
                        </TableRow>
                      ) : (
                        filtered.map((item) => (
                          <TableRow key={item.id} data-testid={`repair-item-row-${item.id}`}>
                            <TableCell className="font-mono font-medium">{item.item_code || item.item_name || '-'}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">{item.category.replace('_', ' ')}</Badge>
                            </TableCell>
                            <TableCell className="max-w-xs">{item.description}</TableCell>
                            <TableCell className="font-bold text-green-600"><CurrencyDisplay amount={item.rate} /></TableCell>
                            <TableCell>
                              <span className={item.amc_type === 'amc' ? 'text-green-700 font-semibold' : 'text-red-600 font-medium'}>
                                {item.amc_type === 'amc' ? 'AMC' : 'Non-AMC'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="text-slate-700 font-medium text-sm">
                                {item.repair_type === 'labour_spares' ? 'Labour + Spares' : item.repair_type === 'outside_fabrication' ? 'Outside Fabrication' : 'Labour Only'}
                              </span>
                            </TableCell>
                            {(canEditRepairItems || canDeleteRepairItems) && (
                              <TableCell>
                                <div className="flex gap-2">
                                  {canEditRepairItems && (
                                    <Button size="sm" variant="outline" onClick={() => handleEditRepairItem(item)}>
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                  )}
                                  {canDeleteRepairItems && (
                                    <Button size="sm" variant="outline" onClick={() => handleDeleteRepairItem(item.id)}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      );
                      })()}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
          )}
        </TabsContent>
        )}
      </Tabs>

      {/* Bike Maintenance History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-4xl'} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle>Maintenance History - {bikeMaintenanceHistory[0]?.bike_plate_number}</DialogTitle>
            <DialogDescription>Complete maintenance and repair history for this motorcycle</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {bikeMaintenanceHistory.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No maintenance records found for this bike</p>
            ) : (
              <div className="space-y-3">
                {bikeMaintenanceHistory.map((record) => (
                  <Card 
                    key={record.id} 
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleViewEstimateDetail(record)}
                  >
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="space-y-2 flex-1">
                          <div className="flex items-center gap-2">
                            <Badge className={getStatusColor(record.status)}>
                              {record.status}
                            </Badge>
                            <span className="text-sm text-slate-600">
                              {formatDateDisplay(record.maintenance_date)} at {record.maintenance_time}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-slate-600">Type:</span>
                              <span className="ml-2 font-medium capitalize">{record.type || record.maintenance_type || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-slate-600">Technician:</span>
                              <span className="ml-2 font-medium">{record.technician || record.technician_name || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-slate-600">Rider:</span>
                              <span className="ml-2 font-medium">{record.rider_name || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-slate-600">Total Cost:</span>
                              <span className="ml-2 font-bold text-green-600"><CurrencyDisplay amount={record.total_cost} /></span>
                            </div>
                          </div>
                          {record.notes && (
                            <p className="text-sm text-slate-600 mt-2">
                              <span className="font-medium">Notes:</span> {record.notes}
                            </p>
                          )}
                        </div>
                        <Button size="sm" variant="outline">
                          View Details →
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end mt-4">
            <Button onClick={closeHistoryDialog}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Estimate Detail Dialog */}
      <Dialog open={estimateDetailDialogOpen} onOpenChange={setEstimateDetailDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-3xl'} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle>Maintenance Estimate Details</DialogTitle>
            <DialogDescription>
              {selectedEstimate?.bike_plate_number} - {formatDateDisplay(selectedEstimate?.maintenance_date)}
            </DialogDescription>
          </DialogHeader>
          {selectedEstimate && (
            <div className="space-y-4">
              {/* Estimate Header Info */}
              <Card>
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600">Status</Label>
                      <div>
                        <Badge className={getStatusColor(selectedEstimate.status)}>
                          {selectedEstimate.status}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-600">Type</Label>
                      <p className="font-medium capitalize">{selectedEstimate.type}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Date & Time</Label>
                      <p className="font-medium">
                        {formatDateDisplay(selectedEstimate.maintenance_date)} at {selectedEstimate.maintenance_time}
                      </p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Technician</Label>
                      <p className="font-medium">{selectedEstimate.technician || 'Not assigned'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Rider</Label>
                      <p className="font-medium">{selectedEstimate.rider_name || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Emirates ID</Label>
                      <p className="font-medium">{selectedEstimate.rider_emirates_id || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Lessee Company</Label>
                      <p className="font-medium">{selectedEstimate.lessee_name || 'N/A'}</p>
                    </div>
                  </div>
                  {selectedEstimate.notes && (
                    <div className="mt-4">
                      <Label className="text-slate-600">Notes</Label>
                      <p className="text-sm mt-1">{selectedEstimate.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Repair Items Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Repair Items & Scope</CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedEstimate.repair_items && selectedEstimate.repair_items.length > 0 ? (
                    <div className="space-y-3">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Item Code</TableHead>
                            <TableHead>Item</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead>Quantity</TableHead>
                            <TableHead className="text-right">Rate (Dh)</TableHead>
                            <TableHead className="text-right">Total (Dh)</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedEstimate.repair_items.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="text-sm text-slate-500">{item.item_code || '-'}</TableCell>
                              <TableCell className="font-medium">
                                {item.item_name}
                                {item.amc_covered && (
                                  <span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-semibold">AMC FREE</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">{item.category}</Badge>
                              </TableCell>
                              <TableCell>{item.quantity}</TableCell>
                              <TableCell className="text-right">{item.rate}</TableCell>
                              <TableCell className="text-right font-bold">
                                {item.amc_covered ? (
                                  <span className="text-green-600 line-through">{(item.quantity * item.rate).toFixed(2)}</span>
                                ) : (
                                  <span className="text-slate-800">{(item.quantity * item.rate).toFixed(2)}</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      
                      {/* Cost Summary with AMC breakdown */}
                      <div className="border-t pt-3 mt-3">
                        <div className="flex flex-col items-end gap-1 text-sm">
                          {(() => {
                            const subtotal = selectedEstimate.repair_items.reduce((sum, item) => sum + (item.rate * (item.quantity || 1)), 0);
                            const amcDiscount = selectedEstimate.repair_items
                              .filter(item => item.amc_covered)
                              .reduce((sum, item) => sum + (item.rate * (item.quantity || 1)), 0);
                            const adminDiscount = selectedEstimate.admin_discount_amount || 0;
                            return (
                              <>
                                <div className="flex gap-8">
                                  <span className="text-slate-500">Subtotal:</span>
                                  <span className="font-semibold w-24 text-right"><CurrencyDisplay amount={subtotal} /></span>
                                </div>
                                {amcDiscount > 0 && (
                                  <div className="flex gap-8 text-green-600">
                                    <span>AMC Free Items Discount:</span>
                                    <span className="font-semibold w-24 text-right">- <CurrencyDisplay amount={amcDiscount} /></span>
                                  </div>
                                )}
                                {adminDiscount > 0 && (
                                  <div className="flex gap-8 text-blue-600">
                                    <span>Admin Discount:</span>
                                    <span className="font-semibold w-24 text-right">- <CurrencyDisplay amount={adminDiscount} /></span>
                                  </div>
                                )}
                                <div className="flex gap-8 border-t pt-1 mt-1">
                                  <span className="text-slate-800 font-bold">Total Cost:</span>
                                  <span className="font-bold text-lg w-24 text-right text-green-600"><CurrencyDisplay amount={selectedEstimate.total_cost} /></span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-4">No repair items recorded</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
          <div className="flex justify-between mt-4">
            <Button variant="outline" onClick={closeEstimateDialog}>
              Back to History
            </Button>
            <Button onClick={closeEstimateDialog}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Floor Screen - TV Display */}
      {/* Apply Changes Confirmation Dialog */}
      <Dialog open={applyChangesDialogOpen} onOpenChange={setApplyChangesDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-md'}`}>
          <DialogHeader>
            <DialogTitle>Apply to Existing Entries?</DialogTitle>
            <DialogDescription>
              The repair item has been updated for new maintenance entries. Would you also like to recalculate all existing maintenance records with the updated rates and AMC classifications?
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 justify-end mt-4">
            <Button variant="outline" onClick={() => setApplyChangesDialogOpen(false)} data-testid="apply-new-only-btn">
              New Entries Only
            </Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleApplyToExisting} data-testid="apply-all-entries-btn">
              Apply to All Existing
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Summary Detail Dialog */}
      <Dialog open={summaryDetailOpen} onOpenChange={setSummaryDetailOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-4xl'} max-h-[85vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle>{summaryDetailData?.title || 'Details'}</DialogTitle>
            <DialogDescription>{summaryDetailData?.records?.length || 0} records found</DialogDescription>
          </DialogHeader>
          {summaryDetailLoading ? (
            <div className="flex justify-center py-8"><Loader className="w-6 h-6 animate-spin" /></div>
          ) : summaryDetailData?.records?.length > 0 ? (
            <div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Plate</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Lessee</TableHead>
                    {summaryDetailData.records[0].rider !== undefined && <TableHead>Rider</TableHead>}
                    {summaryDetailData.records[0].amc_items !== undefined && <TableHead>AMC Items</TableHead>}
                    {summaryDetailData.records[0].discount_amount !== undefined && <TableHead>Discount</TableHead>}
                    {summaryDetailData.records[0].discount_reason !== undefined && <TableHead>Reason</TableHead>}
                    {summaryDetailData.records[0].visits !== undefined && <TableHead>Visits</TableHead>}
                    <TableHead className="text-right">
                      {summaryDetailData.records[0].amc_total !== undefined ? 'AMC Value' : 
                       summaryDetailData.records[0].discount_amount !== undefined ? 'Amount' : 'Cost'}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summaryDetailData.records.map((r, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-slate-400">{idx + 1}</TableCell>
                      <TableCell className="font-medium">{r.plate}</TableCell>
                      <TableCell>{r.date || '-'}</TableCell>
                      <TableCell className="text-sm">{r.lessee || '-'}</TableCell>
                      {r.rider !== undefined && <TableCell className="text-sm">{r.rider}</TableCell>}
                      {r.amc_items !== undefined && (
                        <TableCell className="text-sm text-green-600">
                          {r.amc_items.map(i => i.name).join(', ')}
                        </TableCell>
                      )}
                      {r.discount_amount !== undefined && (
                        <TableCell className="font-semibold text-orange-600"><CurrencyDisplay amount={r.discount_amount} /></TableCell>
                      )}
                      {r.discount_reason !== undefined && <TableCell className="text-sm">{r.discount_reason}</TableCell>}
                      {r.visits !== undefined && <TableCell>{r.visits}</TableCell>}
                      <TableCell className="text-right font-bold">
                        <CurrencyDisplay amount={r.amc_total || r.discount_amount || r.total_cost || 0} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="border-t mt-3 pt-3 flex justify-end">
                <div className="text-sm font-bold">
                  Grand Total: <CurrencyDisplay amount={summaryDetailData.records.reduce((s, r) => s + (r.amc_total || r.discount_amount || r.total_cost || 0), 0)} />
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8">No records found</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin Discount Dialog */}
      <Dialog open={discountDialogOpen} onOpenChange={(open) => { if (!open) { setDiscountDialogOpen(false); setDiscountRecord(null); } }}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-md'}`}>
          <DialogHeader>
            <DialogTitle>Admin Discount</DialogTitle>
            <DialogDescription>
              {discountRecord?.bike_plate_number} - {formatDateDisplay(discountRecord?.maintenance_date)}
            </DialogDescription>
          </DialogHeader>
          {discountRecord && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Original Total Cost</span>
                  <span className="font-semibold"><CurrencyDisplay amount={(discountRecord.total_cost || 0) + (discountRecord.admin_discount_amount || 0)} /></span>
                </div>
                {discountRecord.admin_discount_amount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Current Discount</span>
                    <span>- <CurrencyDisplay amount={discountRecord.admin_discount_amount} /></span>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Discount Amount (Dh) *</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="Enter discount amount"
                  value={discountForm.amount}
                  onChange={(e) => setDiscountForm({ ...discountForm, amount: e.target.value })}
                  data-testid="discount-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Reason for Discount *</Label>
                <Select
                  value={discountForm.reason}
                  onValueChange={(val) => setDiscountForm({ ...discountForm, reason: val, other_reason: val === 'Other' ? discountForm.other_reason : '' })}
                >
                  <SelectTrigger data-testid="discount-reason-select">
                    <SelectValue placeholder="Select a reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    {DISCOUNT_REASONS.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {discountForm.reason === 'Other' && (
                <div className="space-y-2">
                  <Label>Specify Reason *</Label>
                  <Textarea
                    placeholder="Enter reason for giving this discount..."
                    value={discountForm.other_reason}
                    onChange={(e) => setDiscountForm({ ...discountForm, other_reason: e.target.value })}
                    data-testid="discount-reason-other"
                  />
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setDiscountDialogOpen(false); setDiscountRecord(null); }}>Cancel</Button>
                <Button onClick={handleApplyDiscount} className="bg-green-600 hover:bg-green-700" data-testid="apply-discount-btn">
                  Apply Discount
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Floor Screen - TV Display */}
      {/* Maintenance Report — full-screen dialog with monthly aggregates,
          repair-item consumption, AMC breakdown, and recovery split. */}
      <MaintenanceReport open={reportOpen} onOpenChange={setReportOpen} />


      <Dialog open={floorScreenOpen} onOpenChange={setFloorScreenOpen}>
        <DialogContent className="max-w-[98vw] max-h-[98vh] p-0 bg-slate-900">
          <div className="relative h-[98vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6">
              <div className="text-center mb-4">
                <h1 className="text-5xl font-bold text-white tracking-wide">EZY MOBILITY SOLUTIONS MOTORCYCLE RENTAL LLC</h1>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Wrench className="w-12 h-12 text-white" />
                  <div>
                    <h2 className="text-3xl font-bold text-white">Maintenance Floor Dashboard</h2>
                    <p className="text-xl text-blue-100">{formatDateDisplay(new Date())}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="space-y-1">
                    <p className="text-sm text-blue-100">Filter by Location</p>
                    <Select value={floorLocationFilter} onValueChange={setFloorLocationFilter}>
                      <SelectTrigger className="w-60 bg-white/10 border-white/30 text-white text-lg h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Locations</SelectItem>
                        <SelectItem value="Dubai Workshop">Dubai Workshop</SelectItem>
                        <SelectItem value="Sharjah Service Center">Sharjah Service Center</SelectItem>
                        <SelectItem value="Abu Dhabi Branch">Abu Dhabi Branch</SelectItem>
                        <SelectItem value="Ajman Service Point">Ajman Service Point</SelectItem>
                        <SelectItem value="Al Ain Workshop">Al Ain Workshop</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-blue-100">Current Time</p>
                    <p className="text-3xl font-bold text-white">{currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="lg"
                    onClick={() => setFloorScreenOpen(false)}
                    className="text-white hover:bg-white/20"
                  >
                    <X className="w-8 h-8" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-4 p-6 bg-slate-800">
              {(() => {
                const today = new Date().toISOString().split('T')[0];
                let todayRecords = maintenanceRecords.filter(r => r.maintenance_date === today);
                // Apply location filter
                if (floorLocationFilter !== 'all') {
                  todayRecords = todayRecords.filter(r => r.location === floorLocationFilter);
                }
                const scheduled = todayRecords.filter(r => r.status === 'scheduled' || r.status === 'pending').length;
                const inProgress = todayRecords.filter(r => r.status === 'in_progress' || r.status === 'ongoing').length;
                const completed = todayRecords.filter(r => r.status === 'completed').length;
                
                return (
                  <>
                    <div className="bg-yellow-500/20 border-2 border-yellow-500 rounded-lg p-6">
                      <p className="text-yellow-200 text-lg font-medium">Scheduled</p>
                      <p className="text-6xl font-bold text-yellow-400 mt-2">{scheduled}</p>
                    </div>
                    <div className="bg-blue-500/20 border-2 border-blue-500 rounded-lg p-6">
                      <p className="text-blue-200 text-lg font-medium">In Progress</p>
                      <p className="text-6xl font-bold text-blue-400 mt-2">{inProgress}</p>
                    </div>
                    <div className="bg-green-500/20 border-2 border-green-500 rounded-lg p-6">
                      <p className="text-green-200 text-lg font-medium">Completed</p>
                      <p className="text-6xl font-bold text-green-400 mt-2">{completed}</p>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Maintenance Table */}
            <div className="p-6 overflow-y-auto" style={{ height: 'calc(98vh - 280px)' }}>
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-slate-700">
                    <th className="text-left py-4 px-4 text-xl font-bold text-blue-400">Date</th>
                    <th className="text-left py-4 px-4 text-xl font-bold text-blue-400">Bike No.</th>
                    <th className="text-left py-4 px-4 text-xl font-bold text-blue-400">Rider Name</th>
                    <th className="text-left py-4 px-4 text-xl font-bold text-blue-400">Technician</th>
                    <th className="text-left py-4 px-4 text-xl font-bold text-blue-400">Location</th>
                    <th className="text-left py-4 px-4 text-xl font-bold text-blue-400">Scheduled Time</th>
                    <th className="text-left py-4 px-4 text-xl font-bold text-blue-400">Est. Completion</th>
                    <th className="text-left py-4 px-4 text-xl font-bold text-blue-400">Floor Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const today = new Date().toISOString().split('T')[0];
                    let todayRecords = maintenanceRecords.filter(r => r.maintenance_date === today);
                    // Apply location filter
                    if (floorLocationFilter !== 'all') {
                      todayRecords = todayRecords.filter(r => r.location === floorLocationFilter);
                    }
                    todayRecords = todayRecords.sort((a, b) => {
                      // Sort by status: in_progress first, then scheduled, then completed
                      const statusOrder = { 'in_progress': 1, 'ongoing': 1, 'scheduled': 2, 'pending': 2, 'completed': 3 };
                      return (statusOrder[a.status] || 99) - (statusOrder[b.status] || 99);
                    });
                    
                    if (todayRecords.length === 0) {
                      return (
                        <tr>
                          <td colSpan="8" className="text-center py-12">
                            <p className="text-3xl text-slate-500">No maintenance scheduled for today</p>
                          </td>
                        </tr>
                      );
                    }
                    
                    return todayRecords.map((record, index) => {
                      const statusConfig = {
                        'scheduled': { bg: 'bg-yellow-500/20', text: 'text-yellow-300', label: 'Scheduled' },
                        'pending': { bg: 'bg-yellow-500/20', text: 'text-yellow-300', label: 'Scheduled' },
                        'in_progress': { bg: 'bg-blue-500/20', text: 'text-blue-300', label: 'In Progress' },
                        'ongoing': { bg: 'bg-blue-500/20', text: 'text-blue-300', label: 'In Progress' },
                        'completed': { bg: 'bg-green-500/20', text: 'text-green-300', label: 'Completed' }
                      };
                      
                      const config = statusConfig[record.status] || { bg: 'bg-slate-700/20', text: 'text-slate-300', label: record.status };
                      
                      // Calculate estimated completion time
                      const schedTime = record.maintenance_time || '09:00';
                      const [hours, minutes] = schedTime.split(':');
                      const estCompletionTime = `${String(parseInt(hours) + 2).padStart(2, '0')}:${minutes}`;
                      
                      return (
                        <tr key={record.id || index} className="border-b border-slate-700/50 hover:bg-slate-800/50 transition-colors">
                          <td className="py-6 px-4">
                            <p className="text-2xl font-medium text-white">{formatDateDisplay(record.maintenance_date)}</p>
                          </td>
                          <td className="py-6 px-4">
                            <p className="text-3xl font-bold text-blue-300">{record.bike_plate_number}</p>
                          </td>
                          <td className="py-6 px-4">
                            <p className="text-2xl font-medium text-white">{record.rider_name || '-'}</p>
                          </td>
                          <td className="py-6 px-4">
                            <p className="text-2xl font-medium text-purple-300">{record.technician || record.technician_name || 'Unassigned'}</p>
                          </td>
                          <td className="py-6 px-4">
                            <p className="text-2xl font-medium text-amber-300">{record.location || '-'}</p>
                          </td>
                          <td className="py-6 px-4">
                            <p className="text-2xl font-bold text-cyan-300">{record.maintenance_time || '-'}</p>
                          </td>
                          <td className="py-6 px-4">
                            <p className="text-2xl font-medium text-orange-300">{estCompletionTime}</p>
                          </td>
                          <td className="py-6 px-4">
                            <span className={`inline-block px-6 py-3 rounded-lg text-xl font-bold ${config.bg} ${config.text}`}>
                              {config.label}
                            </span>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="absolute bottom-0 left-0 right-0 bg-slate-800 p-4 border-t-2 border-slate-700">
              <p className="text-center text-slate-400 text-lg">
                Auto-refreshing every 30 seconds • Press ESC or click X to exit • Last updated: {currentTime.toLocaleTimeString()}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Maintenance;
